import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import './ViewProductModal.css';
import LabelGenerator from './LabelGenerator';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import StockHistoryModal from './StockHistoryModal';
import GroupPricesModal from './GroupPricesModal';
import OpeningStockModal from './OpeningStockModal';
import StockReport from './StockReport';
/* console.log('Products component loaded'); */

// ── UpdatePriceTab: Export / Import product prices ─────────────────────────
const UpdatePriceTab = ({ products, setProducts }) => {
  const [priceFile, setPriceFile] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [status, setStatus] = React.useState(null); // { type, msg, data, errors }
  const [isExporting, setIsExporting] = React.useState(false);
  const [isImporting, setIsImporting] = React.useState(false);
  const priceInputRef = React.useRef(null);

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExportPrices = async () => {
    setIsExporting(true);
    setStatus(null);

    // 1️⃣ Try backend export API first
    try {
      const res = await apiService.exportProductPrices();
      if (res.success && res.data && res.data.download_url) {
        const blob = await apiService.downloadProductPrices(res.data.download_url);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.data.file_name || `product_prices.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        setStatus({ type: 'success', msg: `✅ ${res.message || 'Exported successfully.'} (${res.data.total_products || 0} products)` });
        setIsExporting(false);
        return;
      }
    } catch (_) {
      // Backend unavailable – fall through to local export
    }

    // 2️⃣ Local fallback: fetch products and generate CSV
    try {
      const data = await apiService.getProducts({ per_page: '-1' });
      const rawList = Array.isArray(data) ? data
        : Array.isArray(data?.data) ? data.data
          : Array.isArray(data?.data?.data) ? data.data.data
            : [];

      if (!rawList.length) {
        setStatus({ type: 'error', msg: 'No products found to export.' });
        setIsExporting(false);
        return;
      }

      // Build rows
      const headers = ['Product Name', 'SKU', 'Selling Price (Including Tax)'];
      const rows = rawList.map(p => {
        const name = p.name || '';
        const sku = p.sku || '';
        let price = '';
        if (p.product_variations?.length) {
          const v = p.product_variations[0]?.variations?.[0];
          price = v ? (v.sell_price_inc_tax ?? '') : '';
        } else {
          price = p.price ?? '';
        }
        const escape = v => `"${String(v).replace(/"/g, '""')}"`;
        return [escape(name), escape(sku), escape(price)].join(',');
      });

      const csv = [headers.join(','), ...rows].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `product_prices_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus({ type: 'success', msg: `✅ Exported ${rawList.length} product(s) successfully (local CSV – backend export unavailable).` });
    } catch (err) {
      setStatus({ type: 'error', msg: `Export failed: ${err.message}` });
    } finally {
      setIsExporting(false);
    }
  };

  // ── Import ────────────────────────────────────────────────────────────────
  const handleImportPrices = async () => {
    if (!priceFile) { setStatus({ type: 'error', msg: 'Please select a file first.' }); return; }

    const formData = new FormData();
    formData.append('file', priceFile);

    setIsImporting(true);
    setStatus(null);

    try {
      const res = await apiService.importProductPrices(formData);
      if (res.success) {
        setStatus({
          type: 'success',
          msg: res.message || 'Product prices imported successfully',
          data: res.data
        });
        setPriceFile(null);
        if (priceInputRef.current) priceInputRef.current.value = '';
      } else {
        setStatus({ type: 'error', msg: res.message || 'Import failed', errors: res.data?.errors || [] });
      }
    } catch (err) {
      setStatus({ type: 'error', msg: 'Failed to import product prices: ' + err.message, errors: [] });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div style={{ padding: '1.5rem', position: 'relative', zIndex: 2 }}>
      <div className="inline-card" style={{ background: 'var(--etpos-card, #fff)', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 className="inline-heading" style={{ margin: '0 0 1.25rem', fontSize: '1.05rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>
          Import / Export Product Price
        </h3>

        {/* Action row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1.5rem', marginBottom: '1.5rem' }}>
          {/* Export */}
          <div>
            <button
              onClick={handleExportPrices}
              disabled={isExporting}
              style={{ padding: '0.55rem 1.3rem', background: isExporting ? '#a78bfa' : '#7c3aed', color: 'white', border: 'none', borderRadius: '7px', cursor: isExporting ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.45rem', boxShadow: '0 2px 8px rgba(124,58,237,0.25)', transition: 'background 0.2s' }}
            >
              {isExporting ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Export product prices
                </>
              )}
            </button>
          </div>

          {/* Import */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>File To Import:</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) setPriceFile(f); }}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}
            >
              <label
                htmlFor="price-file-input"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.45rem 1rem', border: `2px dashed ${dragOver ? '#7c3aed' : 'var(--etpos-text-muted, #cbd5e1)'}`,
                  borderRadius: '7px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500',
                  background: dragOver ? '#f5f3ff' : '#f8fafc', color: 'var(--etpos-text-muted, #475569)', transition: 'all 0.2s',
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {priceFile ? priceFile.name : 'Choose File'}
              </label>
              <input
                ref={priceInputRef}
                id="price-file-input"
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files[0]) { setPriceFile(e.target.files[0]); setStatus(null); } }}
              />
              <button
                onClick={handleImportPrices}
                disabled={isImporting}
                style={{ padding: '0.5rem 1.25rem', background: isImporting ? '#a78bfa' : '#7c3aed', color: 'white', border: 'none', borderRadius: '7px', cursor: isImporting ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', boxShadow: '0 2px 8px rgba(124,58,237,0.2)' }}
              >
                {isImporting ? 'Importing...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>

        {/* Status message */}
        {status && (
          <div style={{ marginBottom: '1.25rem', padding: '1rem', borderRadius: '8px', fontSize: '0.875rem', background: status.type === 'success' ? '#f0fdf4' : '#fef2f2', color: status.type === 'success' ? '#166534' : '#991b1b', border: `1px solid ${status.type === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
            <div style={{ fontWeight: '600', marginBottom: status.errors?.length ? '0.5rem' : '0' }}>{status.msg}</div>

            {status.type === 'success' && status.data && (
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{ background: '#dcfce7', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8rem' }}>Processed: {status.data.total_processed || 0}</span>
                <span style={{ background: '#dcfce7', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8rem' }}>Prods Updated: {status.data.updated_products || 0}</span>
                <span style={{ background: '#dcfce7', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8rem' }}>Groups Updated: {status.data.updated_price_groups || 0}</span>
              </div>
            )}

            {status.errors && status.errors.length > 0 && (
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem', fontSize: '0.8rem' }}>
                {status.errors.map((err, i) => {
                  let identifier = '';
                  if (err.sku) identifier = ` (SKU: ${err.sku})`;
                  else if (err.price_group) identifier = ` (Price Group: ${err.price_group})`;

                  return (
                    <li key={i}>
                      <strong>Row {err.row}{identifier}:</strong> {err.error}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <hr style={{ margin: '0 0 1rem', border: 'none', borderTop: '1px solid #e2e8f0' }} />

        {/* Instructions */}
        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Instructions:</div>
        <ul style={{ margin: '0 0 0 1.1rem', padding: 0, fontSize: '0.82rem', color: 'var(--etpos-text-muted, #475569)', lineHeight: '1.8' }}>
          <li>Export product prices by clicking on the <strong>Export product prices</strong> button above.</li>
          <li>The exported file will contain Product Info, Base Selling Price, and all Selling Price Groups.</li>
          <li><strong>Rules for modification:</strong></li>
          <ul style={{ margin: '0 0 0 1.1rem', padding: 0 }}>
            <li>Do not change Product Name, SKU, or the Header Row.</li>
            <li>Modify ONLY the price columns (Selling Price Including Tax & Price Group columns).</li>
            <li>Prices must be numeric values. Empty cells indicate skipping that attribute.</li>
          </ul>
          <li>Save the file as <strong>.xlsx</strong> or <strong>.xls</strong>, then import it using the <strong>Choose File</strong> button and click <strong>Submit</strong>.</li>
        </ul>
      </div>
    </div>
  );
};

const Products = ({ activeTab: propActiveTab }) => {
  const [activeTab, setActiveTab] = useState('all_products');

  // Synchronize state with prop changes from dashboard popover
  useEffect(() => {
    if (propActiveTab) {
      if (propActiveTab === 'products') {
        setActiveTab('all_products');
      } else {
        setActiveTab(propActiveTab);
      }
    }
  }, [propActiveTab]);

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [lastPage, setLastPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  // Sort State
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [tabSortConfig, setTabSortConfig] = useState({ key: null, direction: 'asc' });

  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);

  const [taxes, setTaxes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [warranties, setWarranties] = useState([]);

  // Variation tab state
  const [variations, setVariations] = useState([]);
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [variationForm, setVariationForm] = useState({ name: '', values: [''] });

  // Import product state
  const [importFile, setImportFile] = useState(null);
  const [importProductStatus, setImportProductStatus] = useState(null);
  const [isImportingProduct, setIsImportingProduct] = useState(false);
  const [isDownloadingProductTemplate, setIsDownloadingProductTemplate] = useState(false);

  // Category form state
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', code: '', description: '', isSubTaxonomy: false, parentCategory: '' });
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [isCategoriesSaving, setIsCategoriesSaving] = useState(false);
  const [categoryApiError, setCategoryApiError] = useState(null);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');

  // Unit form state
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({ actual_name: '', short_name: '', allow_decimal: false });
  const [isUnitsLoading, setIsUnitsLoading] = useState(false);
  const [isUnitsSaving, setIsUnitsSaving] = useState(false);
  const [unitApiError, setUnitApiError] = useState(null);

  // Warranty form state
  const [showWarrantyForm, setShowWarrantyForm] = useState(false);
  const [editingWarranty, setEditingWarranty] = useState(null);
  const [warrantyForm, setWarrantyForm] = useState({ name: '', description: '', duration: '', duration_type: 'Months' });
  const [isWarrantiesSaving, setIsWarrantiesSaving] = useState(false);
  const [warrantyApiError, setWarrantyApiError] = useState(null);

  // Brand form state
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandForm, setBrandForm] = useState({ name: '', description: '', use_for_repair: false });
  const [isBrandsLoading, setIsBrandsLoading] = useState(false);
  const [isBrandsSaving, setIsBrandsSaving] = useState(false);
  const [brandApiError, setBrandApiError] = useState(null);

  // Warranties state
  const [isWarrantiesLoading, setIsWarrantiesLoading] = useState(false);

  // Import Opening Stock state
  const [importOpeningStockStatus, setImportOpeningStockStatus] = useState(null);
  const [isImportingOpeningStock, setIsImportingOpeningStock] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  // Selling Price Group state
  const [sellingPriceGroups, setSellingPriceGroups] = useState([]);
  const [showSPGModal, setShowSPGModal] = useState(false);
  const [spgForm, setSpgForm] = useState({ name: '', description: '' });
  const [editingSPG, setEditingSPG] = useState(null);
  const [isSPGLoading, setIsSPGLoading] = useState(false);
  const [isSPGSaving, setIsSPGSaving] = useState(false);
  const [spgApiError, setSpgApiError] = useState(null);

  // Variation API state
  const [isVariationsLoading, setIsVariationsLoading] = useState(false);
  const [isVariationsSaving, setIsVariationsSaving] = useState(false);
  const [variationApiError, setVariationApiError] = useState(null);
  const [editingVariation, setEditingVariation] = useState(null);

  // Fetch selling price groups from API
  useEffect(() => {
    const fetchSPG = async () => {
      try {
        setIsSPGLoading(true);
        const data = await apiService.getSellingPriceGroups();
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data?.data) ? data.data.data : (Array.isArray(data?.data) ? data.data : []));
        setSellingPriceGroups(list);
      } catch (err) {
        /* console.error('Failed to load selling price groups:', err); */
      } finally {
        setIsSPGLoading(false);
      }
    };
    fetchSPG();

    const fetchLocations = async () => {
      try {
        const data = await apiService.getBusinessLocations();
        const list = Array.isArray(data) ? data : (data.data || []);
        setBusinessLocations(list);
      } catch (err) {
        /* console.error('Failed to load business locations:', err); */
      }
    };
    fetchLocations();
  }, []);

  const extractArray = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.data?.data)) return res.data.data;
    return [];
  };

  // Fetch metadata (categories, units, brands, warranties, taxes)
  const fetchMetadata = useCallback(async () => {
    try {
      setIsCategoriesLoading(true);
      setIsUnitsLoading(true);
      setIsBrandsLoading(true);
      setIsWarrantiesLoading(true);

      const [catData, unitData, taxData, brandData, warrantyData] = await Promise.all([
        apiService.getCategories().catch(() => []),
        apiService.getUnits().catch(() => []),
        apiService.getTaxRates().catch(() => []),
        apiService.getBrands().catch(() => []),
        apiService.getWarranties().catch(() => [])
      ]);

      setCategories(extractArray(catData));
      setUnits(extractArray(unitData));
      setTaxes(extractArray(taxData));
      setBrands(extractArray(brandData));
      setWarranties(extractArray(warrantyData));
    } catch (error) {
      /* console.error("Failed to fetch metadata:", error); */
    } finally {
      setIsCategoriesLoading(false);
      setIsUnitsLoading(false);
      setIsBrandsLoading(false);
      setIsWarrantiesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  useEffect(() => {
    if (['categories', 'units', 'brands', 'warranties', 'tax_rates'].includes(activeTab)) {
      fetchMetadata();
    }
  }, [activeTab, fetchMetadata]);

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const data = await apiService.getProducts({
          page: currentPage,
          per_page: perPage,
          search: searchTerm
        });
        /* console.log('API Response in Products.jsx:', data); */

        const productList = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.data?.data)
              ? data.data.data
              : [];

        // Extract total count and last page from all possible API response formats (string or number)
        const rawTotal = data?.total ?? 
                         data?.meta?.total ?? 
                         data?.meta?.pagination?.total ?? 
                         data?.data?.total ?? 
                         data?.pagination?.total ?? 
                         data?.count ?? 
                         data?.total_count;

        const rawLastPage = data?.last_page ?? 
                            data?.meta?.last_page ?? 
                            data?.meta?.pagination?.total_pages ?? 
                            data?.data?.last_page ?? 
                            data?.pagination?.last_page;

        let total = 0;
        let lastPg = 1;

        if (rawTotal !== undefined && rawTotal !== null && !isNaN(parseInt(rawTotal, 10)) && parseInt(rawTotal, 10) > 0) {
          total = parseInt(rawTotal, 10);
          if (!searchTerm) {
            localStorage.setItem('etpos_products_total_count', String(total));
          }
        } else {
          const cachedTotal = !searchTerm ? localStorage.getItem('etpos_products_total_count') : null;
          if (cachedTotal && !isNaN(parseInt(cachedTotal, 10)) && parseInt(cachedTotal, 10) > 0) {
            total = parseInt(cachedTotal, 10);
          } else {
            total = productList.length;
          }
        }

        if (rawLastPage !== undefined && rawLastPage !== null && !isNaN(parseInt(rawLastPage, 10)) && parseInt(rawLastPage, 10) > 0) {
          lastPg = parseInt(rawLastPage, 10);
        } else {
          lastPg = Math.ceil(total / perPage) || 1;
        }

        setTotalProducts(total);
        setLastPage(lastPg);

        const mappedProducts = productList.map(p => {
          // Extract price/cost from first variation if available
          const firstVariation = p.product_variations?.[0]?.variations?.[0];
          const price = firstVariation ? parseFloat(firstVariation.sell_price_inc_tax) : 0;
          const priceExcTax = firstVariation ? parseFloat(firstVariation.default_sell_price || firstVariation.sell_price_exc_tax) : 0;
          const cost = firstVariation ? parseFloat(firstVariation.default_purchase_price) : 0;
          const variation = firstVariation?.name && firstVariation.name !== 'DUMMY' ? firstVariation.name : 'Default';

          // Extract location name
          const locationName = p.product_locations?.[0]?.name || 'N/A';

          return {
            id: p.id,
            name: p.name,
            description: p.description || '',
            sku: p.sku,
            barcode: p.sku || '', // Using SKU as barcode identifier
            category: p.category?.name || 'N/A',
            location: locationName,
            currentStock: (firstVariation?.variation_location_details?.length > 0)
              ? firstVariation.variation_location_details.reduce((sum, vld) => sum + (parseFloat(vld.qty_available || vld.stock || vld.current_stock) || 0), 0)
              : parseFloat(p.qty_available || p.current_stock || p.stock || p.total_qty_available || firstVariation?.qty_available || firstVariation?.stock || firstVariation?.current_stock || 0),
            unit: p.unit?.actual_name || 'Single',
            price: price,
            priceExcTax: priceExcTax,
            cost: cost,
            variation: variation,
            status: p.is_inactive ? 'Inactive' : 'Active',
            image: p.image_url || null,
            brochure: p.brochure_url || null,
            type: p.type,
            brand: p.brand?.name || 'N/A',
            tax: p.tax || 'None',
            notForSelling: p.not_for_selling === 1,
            woocommerceEnabled: false // Field mapping TBD
          };
        });

        setProducts(mappedProducts);
      } catch (err) {
        /* console.error('Failed to load products', err); */
        setError('Failed to load products');
      } finally {
        setIsLoading(false);
      }
    };

    if (searchTerm) {
      const timeoutId = setTimeout(() => {
        fetchProducts();
      }, 400);
      return () => clearTimeout(timeoutId);
    } else {
      fetchProducts();
    }
  }, [currentPage, perPage, searchTerm, refreshTrigger]);

  const [openActionId, setOpenActionId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedProductForLabel, setSelectedProductForLabel] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedProductForView, setSelectedProductForView] = useState(null);
  const [showStockHistoryModal, setShowStockHistoryModal] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);
  const [showGroupPricesModal, setShowGroupPricesModal] = useState(false);
  const [selectedProductForGroupPrices, setSelectedProductForGroupPrices] = useState(null);
  const [showOpeningStockModal, setShowOpeningStockModal] = useState(false);
  const [selectedProductForOpeningStock, setSelectedProductForOpeningStock] = useState(null);
  const [businessLocations, setBusinessLocations] = useState([]);

  const toggleActionMenu = (id, event) => {
    event.stopPropagation();
    if (openActionId === id) {
      setOpenActionId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 220;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenActionId(id);
    }
  };

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const handleViewProduct = (product) => {
    /* console.log('View product clicked:', product); */
    setSelectedProductForView(product);
    setViewModalOpen(true);
    setOpenActionId(null);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedProductForView(null);
  };

  const handleOpenStockHistory = (product) => {
    setSelectedProductForHistory(product);
    setShowStockHistoryModal(true);
    setOpenActionId(null);
  };

  const handleOpenGroupPrices = (product) => {
    setSelectedProductForGroupPrices(product);
    setShowGroupPricesModal(true);
    setOpenActionId(null);
  };

  const handleOpenOpeningStock = (product) => {
    setSelectedProductForOpeningStock(product);
    setShowOpeningStockModal(true);
    setOpenActionId(null);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      setOpenActionId(null);
      await apiService.deleteProduct(productId);
      toast.success('Product deleted successfully');
      try {
        window.dispatchEvent(new Event('reload-subscription'));
      } catch (e) { }
      setProducts(prev => prev.filter(p => p.id !== productId));
    } catch (error) {
      /* console.error('Error deleting product:', error); */
      toast.error(error.message || 'Failed to delete product');
    }
  };

  const handleSaveGroupPrices = (data, addAnother = false) => {
    /* console.log('Saving Group Prices:', data); */
    // Success notification would go here
    if (!addAnother) {
      setShowGroupPricesModal(false);
      setSelectedProductForGroupPrices(null);
    }
  };

  const handleSaveAndAddOpeningStock = (data) => {
    /* console.log('Saving Group Prices and opening stock modal:', data); */
    setShowGroupPricesModal(false);
    // Transition to opening stock modal
    setTimeout(() => {
      setSelectedProductForOpeningStock(selectedProductForGroupPrices);
      setShowOpeningStockModal(true);
    }, 100);
  };

  const handleSaveOpeningStock = (data) => {
    /* console.log('Saving Opening Stock:', data); */
    setRefreshTrigger(prev => prev + 1);
    setShowOpeningStockModal(false);
    setSelectedProductForOpeningStock(null);
  };

  const handlePrintProduct = () => {
    if (!selectedProductForView) return;

    const p = selectedProductForView;
    const esc = (value) => {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };

    const cost = Number(p.cost ?? 0);
    const price = Number(p.price ?? 0);
    const profitMargin = cost > 0 ? (((price - cost) / cost) * 100) : 0;

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Product - ${esc(p.name)}</title>
    <style>
      * { box-sizing: border-box; }
      body { margin: 0; padding: 24px; font-family: Arial, Helvetica, sans-serif; color: #111827; background: #ffffff; }
      .header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; border-bottom: 2px solid #111827; padding-bottom: 12px; margin-bottom: 16px; }
      .title { font-size: 20px; font-weight: 700; margin: 0; }
      .meta { font-size: 12px; color: #6b7280; margin-top: 4px; }
      .imgWrap { width: 140px; }
      .imgBox { width: 140px; height: 140px; border: 1px solid #e5e7eb; border-radius: 8px; background: #f8fafc; display: flex; align-items: center; justify-content: center; overflow: hidden; }
      .imgBox img { width: 100%; height: 100%; object-fit: contain; display: block; }
      .noImg { font-size: 12px; color: #6b7280; }
      .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 16px; }
      .field { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; min-height: 56px; }
      .label { font-size: 11px; font-weight: 700; color: #6b7280; margin-bottom: 4px; }
      .value { font-size: 13px; color: #111827; white-space: pre-wrap; word-break: break-word; }
      .pricing { border-radius: 10px; overflow: hidden; border: 1px solid #d1fae5; margin-bottom: 16px; }
      .pricing table { width: 100%; border-collapse: collapse; }
      .pricing th { background: #10b981; color: #fff; text-align: left; padding: 10px; font-size: 12px; }
      .pricing td { padding: 10px; border-top: 1px solid #e5e7eb; font-size: 13px; }
      .section { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; }
      .sectionTitle { font-size: 14px; font-weight: 700; margin: 0; }
      @media print {
        @page { size: A4 portrait; margin: 14mm; }
        body { padding: 0; }
      }
    </style>
  </head>
  <body>
    <div class="header">
      <div>
        <h1 class="title">#${esc(p.name)}</h1>
        <div class="meta">Printed: ${esc(new Date().toLocaleString())}</div>
      </div>
      <div class="imgWrap">
        <div class="imgBox">
          ${p.image ? `<img src="${esc(p.image)}" alt="${esc(p.name)}" onerror="this.remove();document.getElementById('noImg').style.display='block';" />` : ''}
          <div id="noImg" class="noImg" style="display:${p.image ? 'none' : 'block'};">No Image</div>
        </div>
      </div>
    </div>

    <div class="grid">
      <div class="field"><div class="label">SKU</div><div class="value">${esc(p.sku || '--')}</div></div>
      <div class="field"><div class="label">Category</div><div class="value">${esc(p.category || '--')}</div></div>
      <div class="field"><div class="label">Brand</div><div class="value">${esc(p.brand || '--')}</div></div>

      <div class="field"><div class="label">Unit</div><div class="value">${esc(p.unit || 'Single')}</div></div>
      <div class="field"><div class="label">Type</div><div class="value">${esc(p.type || 'Single')}</div></div>
      <div class="field"><div class="label">Tax</div><div class="value">${esc(p.tax || 'None')}</div></div>

      <div class="field"><div class="label">Location</div><div class="value">${esc(p.location || 'N/A')}</div></div>
      <div class="field"><div class="label">Current Stock</div><div class="value">${esc(p.currentStock ?? 0)} units</div></div>
      <div class="field"><div class="label">Status</div><div class="value">${esc(p.status || 'Active')}</div></div>

      <div class="field"><div class="label">Not for Selling</div><div class="value">${p.notForSelling ? 'Yes' : 'No'}</div></div>
      <div class="field" style="grid-column: span 2;"><div class="label">Description</div><div class="value">${esc(p.description || 'No description')}</div></div>
    </div>

    <div class="pricing">
      <table>
        <thead>
          <tr>
            <th>Cost Price</th>
            <th>Selling Price</th>
            <th>Profit Margin</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>$ ${esc(cost.toFixed(2))}</td>
            <td>$ ${esc(price.toFixed(2))}</td>
            <td>${esc(profitMargin.toFixed(2))}%</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="section">
      <h2 class="sectionTitle">Product Stock Details</h2>
    </div>

  </body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '1024px';
    iframe.style.height = '768px';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';
    iframe.setAttribute('aria-hidden', 'true');

    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      /* console.error('Unable to access print iframe'); */
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    // Force a layout flush before printing (helps prevent blank prints)
    // and keep the call synchronous to preserve the click gesture.
    if (doc.body) {
      // eslint-disable-next-line no-unused-expressions
      doc.body.offsetHeight;
    }

    win.focus();
    win.print();

    // Cleanup after the dialog opens.
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1500);
  };

  // searchTerm moved to top for use in useEffect



  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  // Export handlers
  const handleExportProducts = (format) => {
    const productsToExport = getSortedProducts(filteredProducts);

    if (!productsToExport || productsToExport.length === 0) {
      alert('No products to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['SKU', 'Name', 'Category', 'Brand', 'Unit', 'Type', 'Tax', 'Price', 'Cost', 'Stock', 'Location', 'Status', 'Description'];
    const rows = productsToExport.map(p => [
      p.sku || '',
      p.name || '',
      p.category || '',
      p.brand || '',
      p.unit || '',
      p.type || '',
      p.tax || '',
      (p.price || 0).toFixed(2),
      (p.cost || 0).toFixed(2),
      (p.currentStock || 0).toFixed(2),
      p.location || '',
      p.status || '',
      (p.description || '').replace(/\n/g, ' ').replace(/"/g, '""')
    ]);

    const title = 'Product List';
    const filename = 'products';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handleExportCategories = (format) => {
    if (!categories || categories.length === 0) {
      alert('No categories to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['#', 'Name', 'Short Code', 'Parent Category'];
    const rows = categories.map((cat, idx) => [
      idx + 1,
      cat.name || cat,
      cat.code || '-',
      cat.parent || '-'
    ]);

    const title = 'Category List';
    const filename = 'categories';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handleExportVariations = (format) => {
    if (!variations || variations.length === 0) {
      alert('No variations to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Name', 'Values'];
    const rows = variations.map(v => [
      v.name || '',
      (v.values || []).join(', ')
    ]);

    const title = 'Variation List';
    const filename = 'variations';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handleExportUnits = (format) => { alert('Export Units'); setShowExportDropdown(false); };
  const handleExportWarranties = (format) => { alert('Export Warranties'); setShowExportDropdown(false); };
  const handleExportBrands = (format) => { alert('Export Brands'); setShowExportDropdown(false); };

  // ── Import Products Template ──────────────────────────────────────────────
  const handleDownloadImportProductTemplate = () => {
    // Serve the real template file placed in /public
    const a = document.createElement('a');
    a.href = `${process.env.PUBLIC_URL}/import_products_template.xls`;
    a.download = 'import_products_template.xls';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleImportProducts = async () => {
    if (!importFile) {
      setImportProductStatus({ type: 'error', msg: 'Please select a file to import.', errors: [] });
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      setIsImportingProduct(true);
      setImportProductStatus(null);
      const res = await apiService.importProducts(formData);

      if (res.success) {
        setImportProductStatus({
          type: 'success',
          msg: res.message || 'Products imported successfully',
          data: res.data,
          errors: res.data?.errors || []
        });
        setImportFile(null);
        const fInput = document.getElementById('product-import-file');
        if (fInput) fInput.value = '';
        setRefreshTrigger(prev => prev + 1); // Refresh product list
      } else {
        setImportProductStatus({ type: 'error', msg: res.message || 'Import failed', errors: res.data?.errors || [] });
      }
    } catch (err) {
      setImportProductStatus({ type: 'error', msg: err.message, errors: [] });
    } finally {
      setIsImportingProduct(false);
    }
  };

  // ── Import Opening Stock Helpers ──────────────────────────────────────────
  const handleDownloadOpeningStockTemplate = async () => {
    setIsDownloadingTemplate(true);

    // 1️⃣ Try backend API first
    try {
      const blob = await apiService.downloadOpeningStockTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'opening_stock_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setIsDownloadingTemplate(false);
      return;
    } catch (_) {
      // API unavailable – generate locally
    }

    // 2️⃣ Local CSV fallback
    const headers = [
      'SKU (Required)',
      'Location (Optional)',
      'Quantity (Required)',
      'Unit Cost (Before Tax) (Required)',
      'Lot Number (Optional)',
      'Expiry Date (Optional)',
    ];

    const sampleRows = [
      ['"TS001"', '"Main Store"', '50', '100', '"LOT001"', '"12-31-2026"'],
      ['"LP001"', '"Main Store"', '10', '1200', '""', '""'],
    ];

    const csv = [
      headers.map(h => `"${h}"`).join(','),
      ...sampleRows.map(r => r.join(','))
    ].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'opening_stock_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setIsDownloadingTemplate(false);
  };

  const handleImportOpeningStock = async () => {
    const fileInput = document.getElementById('opening-stock-file');
    if (!fileInput || !fileInput.files[0]) {
      setImportOpeningStockStatus({ type: 'error', msg: 'Please select a file to import.', errors: [] });
      return;
    }

    const formData = new FormData();
    formData.append('file', fileInput.files[0]);

    try {
      setIsImportingOpeningStock(true);
      setImportOpeningStockStatus(null);
      const res = await apiService.importOpeningStock(formData);

      if (res.success) {
        setImportOpeningStockStatus({
          type: 'success',
          msg: res.message || 'Opening stock imported successfully',
          data: res.data,
          errors: res.errors || res.data?.errors || []
        });
        fileInput.value = ''; // clear
        setRefreshTrigger(prev => prev + 1);
      } else {
        setImportOpeningStockStatus({ type: 'error', msg: res.message || 'Import failed', errors: res.data?.errors || [] });
      }
    } catch (err) {
      setImportOpeningStockStatus({ type: 'error', msg: err.message, errors: [] });
    } finally {
      setIsImportingOpeningStock(false);
    }
  };

  // ── Brand API helpers ──────────────────────────────────────────────────────
  const reloadBrands = async () => {
    try {
      setIsBrandsLoading(true);
      setBrandApiError(null);
      const data = await apiService.getBrands();
      setBrands(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      setBrandApiError('Failed to load brands: ' + err.message);
    } finally {
      setIsBrandsLoading(false);
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;
    try {
      setIsBrandsSaving(true);
      setBrandApiError(null);
      await apiService.deleteBrand(brandId);
      setBrands(prev => prev.filter(b => b.id !== brandId));
    } catch (err) {
      setBrandApiError('Failed to delete brand: ' + err.message);
    } finally {
      setIsBrandsSaving(false);
    }
  };

  const handleSaveBrand = async () => {
    if (!brandForm.name.trim()) { alert('Brand name is required'); return; }
    try {
      setIsBrandsSaving(true);
      setBrandApiError(null);
      if (editingBrand) {
        const res = await apiService.updateBrand(editingBrand.id, brandForm);
        const updated = res.data || res;
        setBrands(prev => prev.map(b => b.id === editingBrand.id ? { ...b, ...updated } : b));
      } else {
        const res = await apiService.createBrand(brandForm);
        const created = res.data || res;
        setBrands(prev => [...prev, created]);
      }
      setShowBrandForm(false);
      setEditingBrand(null);
      setBrandForm({ name: '', description: '', use_for_repair: false });
    } catch (err) {
      setBrandApiError('Failed to save brand: ' + err.message);
    } finally {
      setIsBrandsSaving(false);
    }
  };

  // ── Unit API helpers ───────────────────────────────────────────────────────
  const reloadUnits = async () => {
    try {
      setIsUnitsLoading(true);
      setUnitApiError(null);
      const data = await apiService.getUnits();
      setUnits(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      setUnitApiError('Failed to load units: ' + err.message);
    } finally {
      setIsUnitsLoading(false);
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;
    try {
      setIsUnitsSaving(true);
      setUnitApiError(null);
      await apiService.deleteUnit(unitId);
      setUnits(prev => prev.filter(u => u.id !== unitId));
    } catch (err) {
      setUnitApiError('Failed to delete unit: ' + err.message);
    } finally {
      setIsUnitsSaving(false);
    }
  };

  const handleSaveUnit = async () => {
    if (!unitForm.actual_name?.trim() || !unitForm.short_name?.trim()) {
      alert('Please fill out all required fields');
      return;
    }
    try {
      setIsUnitsSaving(true);
      setUnitApiError(null);

      const payload = {
        actual_name: unitForm.actual_name.trim(),
        short_name: unitForm.short_name.trim(),
        allow_decimal: unitForm.allow_decimal === 'Yes' ? 1 : 0
      };

      if (editingUnit) {
        const res = await apiService.updateUnit(editingUnit.id, payload);
        const updated = res.data || res;
        setUnits(prev => prev.map(u => u.id === editingUnit.id ? { ...u, ...updated } : u));
      } else {
        const res = await apiService.createUnit(payload);
        const created = res.data || res;
        setUnits(prev => [...prev, created]);
      }
      setShowUnitForm(false);
      setEditingUnit(null);
      setUnitForm({ actual_name: '', short_name: '', allow_decimal: false });
    } catch (err) {
      setUnitApiError('Failed to save unit: ' + err.message);
    } finally {
      setIsUnitsSaving(false);
    }
  };

  // ── Warranty API helpers ───────────────────────────────────────────────────
  const reloadWarranties = async () => {
    try {
      setIsWarrantiesLoading(true);
      setWarrantyApiError(null);
      const data = await apiService.getWarranties();
      setWarranties(extractArray(data));
    } catch (err) {
      setWarrantyApiError('Failed to load warranties: ' + err.message);
    } finally {
      setIsWarrantiesLoading(false);
    }
  };

  const handleDeleteWarranty = async (warrantyId) => {
    if (!window.confirm('Are you sure you want to delete this warranty?')) return;
    try {
      setIsWarrantiesSaving(true);
      setWarrantyApiError(null);
      await apiService.deleteWarranty(warrantyId);
      setWarranties(prev => prev.filter(w => w.id !== warrantyId));
    } catch (err) {
      setWarrantyApiError('Failed to delete warranty: ' + err.message);
    } finally {
      setIsWarrantiesSaving(false);
    }
  };

  const handleSaveWarranty = async () => {
    if (!warrantyForm.name.trim() || !warrantyForm.duration || !warrantyForm.duration_type) {
      alert('Please fill out all required fields');
      return;
    }
    try {
      setIsWarrantiesSaving(true);
      setWarrantyApiError(null);
      const payload = {
        name: warrantyForm.name.trim(),
        description: warrantyForm.description ? warrantyForm.description.trim() : '',
        duration: warrantyForm.duration,
        duration_type: warrantyForm.duration_type.toLowerCase()
      };
      if (editingWarranty) {
        const res = await apiService.updateWarranty(editingWarranty.id, payload);
        const updated = res.data || res;
        setWarranties(prev => prev.map(w => w.id === editingWarranty.id ? { ...w, ...updated } : w));
      } else {
        const res = await apiService.createWarranty(payload);
        const created = res.data || res;
        setWarranties(prev => [...prev, created]);
      }
      setShowWarrantyForm(false);
      setEditingWarranty(null);
      setWarrantyForm({ name: '', description: '', duration: '', duration_type: 'Months' });
    } catch (err) {
      setWarrantyApiError('Failed to save warranty: ' + err.message);
    } finally {
      setIsWarrantiesSaving(false);
    }
  };

  // ── Selling Price Group API helpers ────────────────────────────────────────
  const reloadSPG = async () => {
    try {
      setIsSPGLoading(true);
      setSpgApiError(null);
      const data = await apiService.getSellingPriceGroups();
      setSellingPriceGroups(Array.isArray(data) ? data : (Array.isArray(data?.data?.data) ? data.data.data : (Array.isArray(data?.data) ? data.data : [])));
    } catch (err) {
      setSpgApiError('Failed to load selling price groups: ' + err.message);
    } finally {
      setIsSPGLoading(false);
    }
  };

  const handleSaveSPG = async () => {
    if (!spgForm.name.trim()) { alert('Name is required'); return; }
    try {
      setIsSPGSaving(true);
      setSpgApiError(null);
      if (editingSPG) {
        const res = await apiService.updateSellingPriceGroup(editingSPG.id, spgForm);
        const updated = res.data || res;
        setSellingPriceGroups(prev => prev.map(g => g.id === editingSPG.id ? { ...g, ...updated } : g));
      } else {
        const res = await apiService.createSellingPriceGroup(spgForm);
        const created = res.data || res;
        setSellingPriceGroups(prev => [...prev, created]);
      }
      setShowSPGModal(false);
      setEditingSPG(null);
      setSpgForm({ name: '', description: '' });
    } catch (err) {
      setSpgApiError('Failed to save selling price group: ' + err.message);
    } finally {
      setIsSPGSaving(false);
    }
  };

  const handleDeleteSPG = async (id) => {
    if (!window.confirm('Are you sure you want to delete this selling price group?')) return;
    try {
      setIsSPGSaving(true);
      setSpgApiError(null);
      await apiService.deleteSellingPriceGroup(id);
      setSellingPriceGroups(prev => prev.filter(g => g.id !== id));
    } catch (err) {
      setSpgApiError('Failed to delete selling price group: ' + err.message);
    } finally {
      setIsSPGSaving(false);
    }
  };

  const handleEditSPG = (grp) => {
    setEditingSPG(grp);
    setSpgForm({ name: grp.name || '', description: grp.description || '' });
    setShowSPGModal(true);
  };

  const handleToggleSPGStatus = async (grp) => {
    try {
      setIsSPGSaving(true);
      setSpgApiError(null);
      const res = await apiService.activateDeactivateSellingPriceGroup(grp.id, grp.is_active ? 0 : 1);
      const updated = res.data || res;
      setSellingPriceGroups(prev => prev.map(g => g.id === grp.id ? { ...g, ...updated } : g));
    } catch (err) {
      setSpgApiError('Failed to toggle status: ' + err.message);
    } finally {
      setIsSPGSaving(false);
    }
  };

  // ── Category API helpers ───────────────────────────────────────────────────
  const reloadCategories = async () => {
    try {
      setIsCategoriesLoading(true);
      setCategoryApiError(null);
      const data = await apiService.getCategories();
      setCategories(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      setCategoryApiError('Failed to load categories: ' + err.message);
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      setIsCategoriesSaving(true);
      setCategoryApiError(null);
      await apiService.deleteCategory(categoryId);
      setCategories(prev => prev.filter(c => c.id !== categoryId));
    } catch (err) {
      setCategoryApiError('Failed to delete category: ' + err.message);
    } finally {
      setIsCategoriesSaving(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name?.trim()) {
      alert('Category name is required');
      return;
    }
    if (categoryForm.isSubTaxonomy && !categoryForm.parentCategory) {
      alert('Please select a parent category');
      return;
    }

    try {
      setIsCategoriesSaving(true);
      setCategoryApiError(null);

      let parent_id = null;
      if (categoryForm.isSubTaxonomy) {
        // Try to find the parent category id based on the selected name
        // (Assuming parentCategory holds the name from the previous code)
        const pcat = categories.find(c => (c.name || c) === categoryForm.parentCategory);
        parent_id = pcat && pcat.id ? pcat.id : null;
      }

      const payload = {
        name: categoryForm.name.trim(),
        short_code: categoryForm.code.trim(),
        description: categoryForm.description.trim(),
        category_type: 'product',
        add_as_sub_cat: categoryForm.isSubTaxonomy ? 1 : 0,
        parent_id: parent_id
      };

      if (editingCategory) {
        const res = await apiService.updateCategory(editingCategory.id, payload);
        const updated = res.data || res;
        setCategories(prev => prev.map(c => c.id === editingCategory.id ? { ...c, ...updated } : c));
      } else {
        const res = await apiService.createCategory(payload);
        const created = res.data || res;
        setCategories(prev => [...prev, created]);
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', code: '', description: '', isSubTaxonomy: false, parentCategory: '' });
    } catch (err) {
      setCategoryApiError('Failed to save category: ' + err.message);
    } finally {
      setIsCategoriesSaving(false);
    }
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name || cat,
      code: cat.code || cat.short_code || '',
      description: cat.description || '',
      isSubTaxonomy: !!cat.parent_id,
      parentCategory: cat.parent_id ? (categories.find(c => c.id === cat.parent_id)?.name || '') : ''
    });
    setShowCategoryForm(true);
  };

  // Filter States
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterUnit, setFilterUnit] = useState('all');
  const [filterTax, setFilterTax] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // Kept this one

  // Reset pagination and sorting when tab changes
  useEffect(() => {
    setCurrentPage(1);
    setTabSortConfig({ key: null, direction: 'asc' });
  }, [activeTab]);

  const handleTabSort = (key) => {
    let direction = 'asc';
    if (tabSortConfig.key === key && tabSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTabSortConfig({ key, direction });
  };

  const getSortedItems = (items, sortConfig) => {
    if (!sortConfig.key) return items;
    return [...items].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Convert to lowercase if string
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const renderSortableHeader = (key, label) => {
    return (
      <th className="sortable" onClick={() => handleTabSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
          {label}
          {tabSortConfig.key === key ? (
            tabSortConfig.direction === 'asc' ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            )
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 15l5 5 5-5" />
              <path d="M7 9l5-5 5 5" />
            </svg>
          )}
        </div>
      </th>
    );
  };

  // Derived options from products (mocking real data source behavior)
  const uniqueTypes = [...new Set(products.map(p => p.type))];
  // const uniqueBrands = [...new Set(products.map(p => p.brand))];
  // const uniqueUnits = [...new Set(products.map(p => p.unit))];
  const uniqueLocations = [...new Set(products.map(p => p.location))];

  // Categories/Units now fetched from API
  // const categories = ['Ice', 'Vodka', 'Whiskey', 'Rum', 'Beer', 'Wine', 'Other'];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || product.type === filterType;
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    const matchesUnit = filterUnit === 'all' || product.unit === filterUnit;
    const matchesTax = filterTax === 'all' || product.tax === filterTax;
    const matchesBrand = filterBrand === 'all' || product.brand === filterBrand;
    const matchesLocation = filterLocation === 'all' || product.location === filterLocation;
    const matchesStatus = filterStatus === 'all' || product.status === filterStatus;

    return matchesSearch && matchesType && matchesCategory && matchesUnit && matchesTax &&
      matchesBrand && matchesLocation && matchesStatus;
  });

  const navigate = useNavigate();

  const handleAddProduct = () => {
    navigate('/products/add');
  };

  const handleEditProduct = (product) => {
    setOpenActionId(null);
    navigate('/products/add', { state: { editProduct: product } });
  };

  const handleOpenLabelGenerator = (product) => {
    setSelectedProductForLabel(product);
    setShowLabelModal(true);
    setOpenActionId(null); // Close action menu
  };

  const getStockStatus = (stock) => {
    if (stock < 0) return { class: 'critical', text: 'Critical' };
    if (stock < 10) return { class: 'low', text: 'Low' };
    if (stock < 50) return { class: 'medium', text: 'Medium' };
    return { class: 'good', text: 'Good' };
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedProducts = (productsToSort) => {
    if (!sortConfig.key) return productsToSort;

    return [...productsToSort].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert to string for comparison if needed
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      // Handle numeric values
      if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // ── Variation API Helpers ──────────────────────────────────────────────────
  const reloadVariations = async () => {
    try {
      setIsVariationsLoading(true);
      setVariationApiError(null);
      const res = await apiService.getVariations({ per_page: perPage, page: currentPage });
      const list = res.data?.data || res.data || (Array.isArray(res) ? res : []);

      // Transform API response to UI format if needed
      const mapped = list.map(v => ({
        id: v.id,
        name: v.name,
        values: v.values?.map(val => ({ id: val.id, name: val.name })) || []
      }));
      setVariations(mapped);

      if (res.data?.last_page) setLastPage(res.data.last_page);
      if (res.data?.total) setTotalProducts(res.data.total);
    } catch (err) {
      setVariationApiError('Failed to load variations: ' + err.message);
    } finally {
      setIsVariationsLoading(false);
    }
  };

  const handleSaveVariation = async () => {
    if (!variationForm.name.trim()) { alert('Variation name is required'); return; }
    const filledValues = variationForm.values.filter(v => typeof v === 'string' ? v.trim() : v.name.trim());
    if (filledValues.length === 0) { alert('At least one variation value is required'); return; }

    try {
      setIsVariationsSaving(true);
      setVariationApiError(null);

      if (editingVariation) {
        // Prepare data for update based on API spec
        const editValues = {};
        const newValues = [];

        variationForm.values.forEach(v => {
          if (typeof v === 'object' && v.id) {
            editValues[v.id] = v.name.trim();
          } else {
            const valName = typeof v === 'string' ? v.trim() : v.name.trim();
            if (valName) newValues.push(valName);
          }
        });

        const payload = {
          name: variationForm.name.trim(),
          variation_values: newValues,
          edit_variation_values: editValues
        };

        await apiService.updateVariation(editingVariation.id, payload);
      } else {
        const payload = {
          name: variationForm.name.trim(),
          variation_values: filledValues.map(v => typeof v === 'string' ? v.trim() : v.name.trim())
        };
        await apiService.createVariation(payload);
      }

      setShowVariationModal(false);
      setEditingVariation(null);
      setVariationForm({ name: '', values: [''] });
      reloadVariations();
    } catch (err) {
      setVariationApiError('Failed to save variation: ' + err.message);
    } finally {
      setIsVariationsSaving(false);
    }
  };

  const handleDeleteVariationValue = async (variationId, valueId) => {
    if (!window.confirm('Delete this value? It might be in use.')) return;
    try {
      setIsVariationsSaving(true);
      await apiService.deleteVariationValue(variationId, valueId);
      reloadVariations();
    } catch (err) {
      setVariationApiError('Failed to delete value: ' + err.message);
    } finally {
      setIsVariationsSaving(false);
    }
  };

  const handleDeleteVariation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this variation?')) return;
    try {
      setIsVariationsSaving(true);
      setVariationApiError(null);
      const res = await apiService.deleteVariation(id);
      if (res.success) {
        setVariations(prev => prev.filter(v => v.id !== id));
      } else {
        setVariationApiError(res.message || 'Failed to delete variation');
      }
    } catch (err) {
      setVariationApiError('Failed to delete variation: ' + err.message);
    } finally {
      setIsVariationsSaving(false);
    }
  };

  const handleEditVariation = (v) => {
    setEditingVariation(v);
    setVariationForm({
      name: v.name,
      values: v.values.length > 0 ? [...v.values] : ['']
    });
    setShowVariationModal(true);
  };

  // Update variation fetch on tab change
  useEffect(() => {
    if (activeTab === 'variation') {
      reloadVariations();
    }
  }, [activeTab, currentPage, perPage]);



  return (
    <div className="products-container">
      {/* Main Card: Tabs + Table Controls + Table */}
      <div className="main-card">


        {/* Import/Export Product Price Tab */}
        {activeTab === 'update_price' && (
          <UpdatePriceTab products={products} setProducts={setProducts} />
        )}

        {activeTab === 'stock_report' && (
          <StockReport />
        )}

        {activeTab === 'variation' && (() => {
          const sortedVariations = getSortedItems(variations, tabSortConfig);
          const totalVariationsPages = Math.ceil(sortedVariations.length / perPage) || 1;
          const paginatedVariations = sortedVariations.slice(
            (currentPage - 1) * perPage,
            currentPage * perPage
          );

          return (
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div className="table-controls">
                <div className="table-controls-left">
                  <div className="entries-dropdown">
                    <span>Show</span>
                    <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                      <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>
                </div>
                <div className="table-controls-right">
                  <div className="export-dropdown-container" style={{ position: 'relative' }}>
                    <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Export ▾
                    </button>
                    {showExportDropdown && (
                      <div className="export-dropdown-menu">
                        <button onClick={() => handleExportVariations('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                        <button onClick={() => handleExportVariations('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                        <button onClick={() => handleExportVariations('Print')}><i className="fa fa-print"></i> Print</button>
                        <button onClick={() => handleExportVariations('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                      </div>
                    )}
                  </div>
                  <button className="control-btn-primary" onClick={() => { setEditingVariation(null); setShowVariationModal(true); setVariationForm({ name: '', values: [''] }); }}>
                    <span className="plus-icon">+</span> Add Variation
                  </button>
                </div>
              </div>

              {variationApiError && (
                <div style={{ margin: '0 1rem 1rem', padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{variationApiError}</span>
                  <button onClick={() => reloadVariations()} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem' }}>Retry</button>
                </div>
              )}

              <div className="products-table-container" style={{ overflowY: 'visible' }}>
                {isVariationsLoading ? (
                  <div className="loading-sales">
                    <h3>⏳ Loading Variations...</h3>
                    <p>Fetching variations data from server...</p>
                  </div>
                ) : paginatedVariations.length === 0 ? (
                  <div className="no-sales">
                    <h3>📊 No Variations Found</h3>
                    <p>No variations have been created yet.</p>
                  </div>
                ) : (
                  <table className="products-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        {renderSortableHeader('name', 'Name')}
                        <th>Values</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedVariations.map((v) => (
                        <tr key={v.id}>
                          <td style={{ fontWeight: '500' }}>{v.name}</td>
                          <td>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                              {v.values.map((val, i) => (
                                <span key={i} style={{ padding: '0.2rem 0.6rem', background: 'var(--etpos-info-bg, #eff6ff)', color: 'var(--etpos-info-text-active, #2563eb)', borderRadius: '12px', fontSize: '0.8rem', border: '1px solid #bfdbfe' }}>
                                  {typeof val === 'object' ? val.name : val}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <button
                              onClick={() => handleEditVariation(v)}
                              style={{ padding: '0.3rem 0.75rem', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.5rem' }}
                            >Edit</button>
                            <button
                              onClick={() => handleDeleteVariation(v.id)}
                              style={{ padding: '0.3rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                              disabled={isVariationsSaving}
                            >Delete</button>
                          </td>
                        </tr>
                      ))
                      }
                    </tbody>
                  </table>
                )}

                {/* Pagination Footer */}
                {!isVariationsLoading && sortedVariations.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                      Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, sortedVariations.length)} of {sortedVariations.length} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#64748b' }}>
                        Page {currentPage} of {totalVariationsPages}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.min(totalVariationsPages, p + 1))}
                        disabled={currentPage === totalVariationsPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: 'var(--etpos-info-bg, #eff6ff)', border: '1px solid #bfdbfe', borderRadius: '10px' }}>
                <h4 style={{ margin: '0 0 0.75rem', color: 'var(--etpos-info-text, #1e40af)', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="fa fa-info-circle"></i> Variation Management Guidelines
                </h4>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--etpos-info-text, #1e40af)', fontSize: '0.82rem', lineHeight: '1.6' }}>
                  <li><strong>Add Variation:</strong> Create new variation templates like "Size" or "Color" with their respective values.</li>
                  <li><strong>Edit Variation:</strong> You can update the variation name and add new values to existing templates.</li>
                  <li><strong>Delete Variation:</strong> A variation can only be deleted if it is not currently used in any products.</li>
                  <li><strong>Usage:</strong> These variations will be available when adding or editing "Variable" type products.</li>
                </ul>
              </div>
            </div>
          );
        })()}

        {activeTab === 'import_product' && (
          <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* ── Import Product ───────────────────────────────────────── */}
            <div className="inline-card" style={{ background: 'var(--etpos-card, #fff)', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem 1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <h4 className="inline-heading" style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fa fa-box" style={{ color: '#7c3aed' }}></i> Import Product
              </h4>
              {/* File picker + Submit + Download — all one row */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.3rem' }}>File To Import:</label>
                  <input
                    id="product-import-file"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0] || null)}
                    style={{ display: 'block', padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', background: '#f9fafb', cursor: 'pointer' }}
                  />
                </div>
                <button
                  onClick={handleImportProducts}
                  disabled={isImportingProduct}
                  style={{ padding: '0.5rem 1.25rem', background: isImportingProduct ? '#a78bfa' : '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: isImportingProduct ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}
                >
                  {isImportingProduct ? 'Importing...' : 'Submit'}
                </button>
                <button
                  onClick={handleDownloadImportProductTemplate}
                  disabled={isDownloadingProductTemplate}
                  style={{ padding: '0.5rem 1.15rem', background: isDownloadingProductTemplate ? '#34d399' : '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: isDownloadingProductTemplate ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(16,185,129,0.2)' }}
                >
                  {isDownloadingProductTemplate ? (
                    'Downloading...'
                  ) : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download template file
                    </>
                  )}
                </button>
              </div>

              {importProductStatus && (
                <div style={{ marginBottom: '1.25rem', padding: '1rem', borderRadius: '8px', fontSize: '0.875rem', background: importProductStatus.type === 'success' ? '#f0fdf4' : '#fef2f2', color: importProductStatus.type === 'success' ? '#166534' : '#991b1b', border: `1px solid ${importProductStatus.type === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
                  <div style={{ fontWeight: '600', marginBottom: importProductStatus.errors?.length ? '0.5rem' : '0' }}>{importProductStatus.msg}</div>

                  {importProductStatus.data && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem' }}>
                      <span style={{ background: importProductStatus.type === 'success' ? '#dcfce7' : '#fee2e2', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8rem' }}>Processed: {importProductStatus.data.total_products || 0}</span>
                      <span style={{ background: importProductStatus.type === 'success' ? '#dcfce7' : '#fee2e2', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8rem' }}>Imported: {importProductStatus.data.imported || 0}</span>
                    </div>
                  )}

                  {importProductStatus.errors && importProductStatus.errors.length > 0 && (
                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem', fontSize: '0.8rem' }}>
                      {importProductStatus.errors.map((err, i) => (
                        <li key={i}>Row {err.row}: {err.error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <hr style={{ margin: '0 0 0.75rem', border: 'none', borderTop: '1px solid #e2e8f0' }} />
              <p style={{ margin: '0 0 0.2rem', fontSize: '0.8rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Instructions</p>
              <p style={{ margin: '0 0 0.65rem', fontSize: '0.8rem', color: '#6b7280' }}>Follow the instructions carefully before importing the file.<br />The columns of the file should be in the following order.</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr className="table-header-row" style={{ background: 'var(--etpos-border, #f1f5f9)' }}>
                      <th style={{ padding: '0.6rem 0.85rem', textAlign: 'center', fontWeight: '700', color: 'var(--etpos-text-muted, #374151)', border: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Column Number</th>
                      <th style={{ padding: '0.6rem 0.85rem', textAlign: 'left', fontWeight: '700', color: 'var(--etpos-text-muted, #374151)', border: '1px solid #e2e8f0' }}>Column Name</th>
                      <th style={{ padding: '0.6rem 0.85rem', textAlign: 'left', fontWeight: '700', color: 'var(--etpos-text-muted, #374151)', border: '1px solid #e2e8f0' }}>Instruction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { num: 1, name: 'Product Name (Required)', note: 'Name of the product' },
                      { num: 2, name: 'Brand (Optional)', note: 'Name of the brand\n(If not found new brand with the given name will be created)' },
                      { num: 3, name: 'Unit (Required)', note: 'Name of the unit' },
                      { num: 4, name: 'Category (Optional)', note: 'Name of the Category\n(If not found new category with the given name will be created)' },
                      { num: 5, name: 'Sub category (Optional)', note: 'Name of the Sub-Category\n(If not found new sub-category with the given name under the parent Category will be created)' },
                      { num: 6, name: 'SKU (Optional)', note: 'Product SKU. If blank an SKU will be automatically generated' },
                      { num: 7, name: 'Barcode Type (Optional, Default: C128)', note: 'Barcode Type for the product.\nCurrently supported: C128, C39, EAN-13, EAN-8, UPC-A, UPC-E, ITF-14' },
                      { num: 8, name: 'Manage Stock? (Required)', note: 'Enable or disable stock management\n1 = Yes\n0 = No' },
                      { num: 9, name: 'Alert quantity (Optional)', note: 'Alert quantity' },
                      { num: 10, name: 'Expires in (Optional)', note: 'Product expiry period (Only in numbers)' },
                      { num: 11, name: 'Expiry Period Unit (Optional)', note: 'Unit for the expiry period\nAvailable Options: days, months' },
                      { num: 12, name: 'Applicable Tax (Optional)', note: 'Name of the Tax Rate\n\nIf purchase Price (Excluding Tax) is not same as Purchase Price (Including Tax) then you must supply the tax rate name.' },
                      { num: 13, name: 'Selling Price Tax Type (Required)', note: 'Selling Price Tax Type\nAvailable Options: inclusive, exclusive' },
                      { num: 14, name: 'Product Type (Required)', note: 'Product Type\nAvailable Options: single, variable' },
                      { num: 15, name: 'Variation Name (Required if product type is variable)', note: 'Name of the variation (Ex: Size, Color etc)' },
                      { num: 16, name: 'Variation Values (Required if product type is variable)', note: "Values for the variation separated with '|'\n(Ex: Red|Blue|Green)" },
                      { num: 17, name: 'Variation SKUs (Optional)', note: "SKUs of each variations separated by '|' if product type is variable" },
                      { num: 18, name: 'Purchase Price (Including Tax)\n(Required if Purchase Price Excluding Tax is not given)', note: "Purchase Price (Including Tax) (Only in numbers)\n\nFor variable products '|' separated values with the same order as Variation Values\n(Ex: 84|85|88)" },
                      { num: 19, name: 'Purchase Price (Excluding Tax)\n(Required if Purchase Price Including Tax is not given)', note: "Purchase Price (Excluding Tax) (Only in numbers)\n\nFor variable products '|' separated values with the same order as Variation Values\n(Ex: 84|85|88)" },
                      { num: 20, name: 'Profit Margin % (Optional)', note: 'Profit Margin (Only in numbers)\nIf blank default profit margin for the business will be used' },
                      { num: 21, name: 'Selling Price (Optional)', note: 'Selling Price (Only in numbers)\nIf blank selling price will be calculated with the given Purchase Price and Applicable Tax' },
                      { num: 22, name: 'Opening Stock (Optional)', note: "Opening Stock (Only in numbers)\n\nFor variable products separate stock quantities with '|'\n(Ex: 100|150|200)" },
                      { num: 23, name: 'Opening stock location (Optional)\nIf blank first business location will be used', note: 'Name of the business location' },
                      { num: 24, name: 'Expiry Date (Optional)', note: 'Stock Expiry Date\nFormat: mm-dd-yyyy; Ex: 11-25-2018' },
                      { num: 25, name: 'Enable Product description, IMEI or Serial Number (Optional, Default: 0)', note: '1 = Yes\n0 = No' },
                      { num: 26, name: 'Weight (Optional)', note: 'Optional' },
                      { num: 27, name: 'Rack (Optional)', note: "Rack details separated by '|' for different business locations serially.\n(Ex: R1|R5|R12)" },
                      { num: 28, name: 'Row (Optional)', note: "Row details separated by '|' for different business locations serially.\n(Ex: ROW1|ROW2|ROW3)" },
                      { num: 29, name: 'Position (Optional)', note: "Position details separated by '|' for different business locations serially.\n(Ex: POS1|POS2|POS3)" },
                      { num: 30, name: 'Image (Optional)', note: 'Image name with extension.\n(Image name must be uploaded to the server public/uploads/img)\n\nOr URL of the image' },
                      { num: 31, name: 'Product Description (Optional)', note: '' },
                      { num: 32, name: 'Custom Field1 (Optional)', note: '' },
                      { num: 33, name: 'Custom Field2 (Optional)', note: '' },
                      { num: 34, name: 'Custom Field3 (Optional)', note: '' },
                      { num: 35, name: 'Custom Field4 (Optional)', note: '' },
                      { num: 36, name: 'Not for selling (Optional)', note: '1 = Yes\n0 = No' },
                      { num: 37, name: 'Product locations (Optional)', note: 'Comma separated string of business location names where product will be available' },
                    ].map((row) => (
                      <tr key={row.num} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '0.6rem 0.85rem', color: 'var(--etpos-text-muted, #374151)', border: '1px solid #e2e8f0', textAlign: 'center', fontWeight: '600' }}>{row.num}</td>
                        <td style={{ padding: '0.6rem 0.85rem', color: 'var(--etpos-text, #1e293b)', border: '1px solid #e2e8f0', fontWeight: '500', whiteSpace: 'pre-line', verticalAlign: 'top' }}>{row.name}</td>
                        <td style={{ padding: '0.6rem 0.85rem', color: '#6b7280', border: '1px solid #e2e8f0', whiteSpace: 'pre-line', verticalAlign: 'top' }}>{row.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Import Opening Stock ──────────────────────────────────── */}
            <div className="inline-card" style={{ background: 'var(--etpos-card, #fff)', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem 1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <h4 className="inline-heading" style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fa fa-warehouse" style={{ color: '#10b981' }}></i> Import Opening Stock
              </h4>
              {/* File picker + Submit + Download — all one row */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.3rem' }}>File To Import:</label>
                  <input
                    id="opening-stock-file"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    style={{ display: 'block', padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', background: '#f9fafb', cursor: 'pointer' }}
                  />
                </div>
                <button
                  onClick={handleImportOpeningStock}
                  disabled={isImportingOpeningStock}
                  style={{ padding: '0.5rem 1.25rem', background: isImportingOpeningStock ? '#a78bfa' : '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: isImportingOpeningStock ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}
                >
                  {isImportingOpeningStock ? 'Importing...' : 'Submit'}
                </button>
                <button
                  onClick={handleDownloadOpeningStockTemplate}
                  disabled={isDownloadingTemplate}
                  style={{ padding: '0.5rem 1.15rem', background: isDownloadingTemplate ? '#34d399' : '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: isDownloadingTemplate ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(16,185,129,0.2)' }}
                >
                  {isDownloadingTemplate ? (
                    'Downloading...'
                  ) : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download template file
                    </>
                  )}
                </button>
              </div>

              {importOpeningStockStatus && (
                <div style={{ marginBottom: '1.25rem', padding: '1rem', borderRadius: '8px', fontSize: '0.875rem', background: importOpeningStockStatus.type === 'success' ? '#f0fdf4' : '#fef2f2', color: importOpeningStockStatus.type === 'success' ? '#166534' : '#991b1b', border: `1px solid ${importOpeningStockStatus.type === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
                  <div style={{ fontWeight: '600', marginBottom: importOpeningStockStatus.errors?.length ? '0.5rem' : '0' }}>{importOpeningStockStatus.msg}</div>
                  {importOpeningStockStatus.type === 'success' && importOpeningStockStatus.data && (
                    <div style={{ marginTop: '0.5rem' }}>
                      Imported {importOpeningStockStatus.data.imported || 0} out of {importOpeningStockStatus.data.total_rows || 0} rows.
                    </div>
                  )}
                  {importOpeningStockStatus.errors && importOpeningStockStatus.errors.length > 0 && (
                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem', fontSize: '0.8rem' }}>
                      {importOpeningStockStatus.errors.map((err, i) => (
                        <li key={i}>Row {err.row}: {err.error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <hr style={{ margin: '0 0 1rem', border: 'none', borderTop: '1px solid #e2e8f0' }} />

              <div style={{ marginBottom: '1.25rem', padding: '1rem', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '8px' }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: '700', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="fa fa-info-circle"></i> Guidelines
                </p>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.8rem', color: '#92400e', lineHeight: '1.4' }}>
                  <li><strong>SKU:</strong> Must match an existing product SKU in the system.</li>
                  <li><strong>Location:</strong> If left blank, the first business location will be assigned.</li>
                  <li><strong>Quantity:</strong> Enter a numeric value (positive number).</li>
                  <li><strong>Unit Cost:</strong> Cost of the item before tax. (Numeric value).</li>
                  <li><strong>Expiry Date:</strong> Use the format <code>mm/dd/yyyy</code> (e.g., 04/19/2026).</li>
                </ul>
              </div>

              <p style={{ margin: '0 0 0.2rem', fontSize: '0.8rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Instructions</p>
              <p style={{ margin: '0 0 0.65rem', fontSize: '0.8rem', color: '#6b7280' }}>Follow the instructions carefully before importing the file.<br />The columns of the file should be in the following order.</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr className="table-header-row" style={{ background: 'var(--etpos-border, #f1f5f9)' }}>
                      <th style={{ padding: '0.6rem 0.85rem', textAlign: 'center', fontWeight: '700', color: 'var(--etpos-text-muted, #374151)', border: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Column Number</th>
                      <th style={{ padding: '0.6rem 0.85rem', textAlign: 'left', fontWeight: '700', color: 'var(--etpos-text-muted, #374151)', border: '1px solid #e2e8f0' }}>Column Name</th>
                      <th style={{ padding: '0.6rem 0.85rem', textAlign: 'left', fontWeight: '700', color: 'var(--etpos-text-muted, #374151)', border: '1px solid #e2e8f0' }}>Instruction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { num: 1, name: 'SKU (Required)', note: '' },
                      { num: 2, name: 'Location (Optional)\nIf blank first business location will be used', note: 'Name of the business location' },
                      { num: 3, name: 'Quantity (Required)', note: '' },
                      { num: 4, name: 'Unit Cost (Before Tax) (Required)', note: '' },
                      { num: 5, name: 'Lot Number (Optional)', note: '' },
                      { num: 6, name: 'Expiry Date (Optional)', note: 'Stock expiry date in Business date format\nmm/dd/yyyy, Type: text, Example: 02/26/2026' },
                    ].map((row) => (
                      <tr key={row.num} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '0.6rem 0.85rem', color: 'var(--etpos-text-muted, #374151)', border: '1px solid #e2e8f0', textAlign: 'center', fontWeight: '600' }}>{row.num}</td>
                        <td style={{ padding: '0.6rem 0.85rem', color: 'var(--etpos-text, #1e293b)', border: '1px solid #e2e8f0', fontWeight: '500', whiteSpace: 'pre-line', verticalAlign: 'top' }}>{row.name}</td>
                        <td style={{ padding: '0.6rem 0.85rem', color: '#6b7280', border: '1px solid #e2e8f0', whiteSpace: 'pre-line', verticalAlign: 'top' }}>{row.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

        {activeTab === 'categories' && (() => {
          const filteredCategories = categories.filter(cat =>
            (cat.name || cat).toLowerCase().includes(categorySearchQuery.toLowerCase()) ||
            (cat.short_code && cat.short_code.toLowerCase().includes(categorySearchQuery.toLowerCase())) ||
            (cat.code && cat.code.toLowerCase().includes(categorySearchQuery.toLowerCase()))
          );
          const sortedCategories = getSortedItems(filteredCategories, tabSortConfig);
          const totalCategoryPages = Math.ceil(sortedCategories.length / perPage) || 1;
          const paginatedCategories = sortedCategories.slice((currentPage - 1) * perPage, currentPage * perPage);

          return (
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div className="table-controls">
                <div className="table-controls-left">
                  <div className="entries-dropdown">
                    <span>Show</span>
                    <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                      <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div style={{ position: 'relative', minWidth: '220px', marginLeft: '1rem' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ position: 'absolute', top: '50%', left: '12px', transform: 'translateY(-50%)' }}>
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={categorySearchQuery}
                      onChange={(e) => { setCategorySearchQuery(e.target.value); setCurrentPage(1); }}
                      style={{ padding: '0.5rem 1rem 0.5rem 2.2rem', width: '100%', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}
                    />
                  </div>
                </div>
                <div className="table-controls-right">
                  <div className="export-dropdown-container" style={{ position: 'relative' }}>
                    <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Export ▾
                    </button>
                    {showExportDropdown && (
                      <div className="export-dropdown-menu">
                        <button onClick={() => handleExportCategories('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                        <button onClick={() => handleExportCategories('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                        <button onClick={() => handleExportCategories('Print')}><i className="fa fa-print"></i> Print</button>
                        <button onClick={() => handleExportCategories('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                      </div>
                    )}
                  </div>
                  <button className="control-btn-primary" onClick={() => { setEditingCategory(null); setShowCategoryForm(true); setCategoryForm({ name: '', code: '', description: '', isSubTaxonomy: false, parentCategory: '' }); }}>
                    <span className="plus-icon">+</span> Add Category
                  </button>
                </div>
              </div>
              {categoryApiError && (
                <div style={{ margin: '0 1rem 0.75rem', padding: '0.65rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{categoryApiError}</span>
                  <button onClick={() => { setCategoryApiError(null); reloadCategories(); }} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.65rem', cursor: 'pointer', fontSize: '0.8rem' }}>Retry</button>
                </div>
              )}
              <div className="products-table-container" style={{ overflowY: 'visible' }}>
                {isCategoriesLoading ? (
                  <div className="loading-sales">
                    <h3>⏳ Loading Categories...</h3>
                    <p>Fetching categories data from server...</p>
                  </div>
                ) : filteredCategories.length === 0 ? (
                  <div className="no-sales">
                    <h3>📊 No Categories Found</h3>
                    <p>{categorySearchQuery ? "No matching categories found." : "No categories have been created yet."}</p>
                  </div>
                ) : (
                  <table className="products-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        {renderSortableHeader('name', 'Name')}
                        {renderSortableHeader('short_code', 'Short Code')}
                        {renderSortableHeader('parent_id', 'Parent Category')}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCategories.map((cat, idx) => {
                        const parentName = cat.parent_id ? (categories.find(c => c.id === cat.parent_id)?.name || cat.parent_id) : '-';
                        return (
                          <tr key={cat.id || idx}>
                            <td>{(currentPage - 1) * perPage + idx + 1}</td>
                            <td style={{ fontWeight: '500' }}>{cat.name || cat}</td>
                            <td>{cat.short_code || cat.code || '-'}</td>
                            <td>{parentName}</td>
                            <td>
                              <button onClick={() => handleEditCategory(cat)} style={{ padding: '0.3rem 0.75rem', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.4rem' }}>Edit</button>
                              <button onClick={() => handleDeleteCategory(cat.id)} style={{ padding: '0.3rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Delete</button>
                            </td>
                          </tr>
                        );
                      })
                      }
                    </tbody>
                  </table>
                )}
                {!isCategoriesLoading && filteredCategories.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                      Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, filteredCategories.length)} of {filteredCategories.length} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#64748b' }}>
                        Page {currentPage} of {totalCategoryPages}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.min(totalCategoryPages, p + 1))}
                        disabled={currentPage === totalCategoryPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {activeTab === 'units' && (() => {
          const sortedUnits = getSortedItems(units, tabSortConfig);
          const totalUnitsPages = Math.ceil(sortedUnits.length / perPage) || 1;
          const paginatedUnits = sortedUnits.slice(
            (currentPage - 1) * perPage,
            currentPage * perPage
          );

          return (
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div className="table-controls">
                <div className="table-controls-left">
                  <div className="entries-dropdown">
                    <span>Show</span>
                    <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                      <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>
                </div>
                <div className="table-controls-right">
                  <div className="export-dropdown-container" style={{ position: 'relative' }}>
                    <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Export ▾
                    </button>
                    {showExportDropdown && (
                      <div className="export-dropdown-menu">
                        <button onClick={() => handleExportUnits('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                        <button onClick={() => handleExportUnits('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                        <button onClick={() => handleExportUnits('Print')}><i className="fa fa-print"></i> Print</button>
                        <button onClick={() => handleExportUnits('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                      </div>
                    )}
                  </div>
                  <button className="control-btn-primary" onClick={() => { setEditingUnit(null); setShowUnitForm(true); setUnitForm({ actual_name: '', short_name: '', allow_decimal: '' }); }}>
                    <span className="plus-icon">+</span> Add Unit
                  </button>
                </div>
              </div>
              {unitApiError && (
                <div style={{ margin: '0 1rem 0.75rem', padding: '0.65rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{unitApiError}</span>
                  <button onClick={() => { setUnitApiError(null); reloadUnits(); }} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.65rem', cursor: 'pointer', fontSize: '0.8rem' }}>Retry</button>
                </div>
              )}
              <div className="products-table-container" style={{ overflowY: 'visible' }}>
                {isUnitsLoading ? (
                  <div className="loading-sales">
                    <h3>⏳ Loading Units...</h3>
                    <p>Fetching units data from server...</p>
                  </div>
                ) : paginatedUnits.length === 0 ? (
                  <div className="no-sales">
                    <h3>📊 No Units Found</h3>
                    <p>No units have been created yet.</p>
                  </div>
                ) : (
                  <table className="products-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        {renderSortableHeader('actual_name', 'Name')}
                        {renderSortableHeader('short_name', 'Short Name')}
                        {renderSortableHeader('allow_decimal', 'Allow Decimal')}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUnits.map((unit, idx) => (
                        <tr key={unit.id || idx}>
                          <td>{(currentPage - 1) * perPage + idx + 1}</td>
                          <td style={{ fontWeight: '500' }}>{unit.actual_name || unit.name || unit}</td>
                          <td>{unit.short_name || '-'}</td>
                          <td>{unit.allow_decimal ? 'Yes' : 'No'}</td>
                          <td>
                            <button onClick={() => { setEditingUnit(unit); setUnitForm({ actual_name: unit.actual_name || unit.name || '', short_name: unit.short_name || '', allow_decimal: unit.allow_decimal ? 'Yes' : 'No' }); setShowUnitForm(true); }} style={{ padding: '0.3rem 0.75rem', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.4rem' }}>Edit</button>
                            <button onClick={() => handleDeleteUnit(unit.id)} style={{ padding: '0.3rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Delete</button>
                          </td>
                        </tr>
                      ))
                      }
                    </tbody>
                  </table>
                )}

                {/* Pagination Footer */}
                {!isUnitsLoading && sortedUnits.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                      Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, sortedUnits.length)} of {sortedUnits.length} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#64748b' }}>
                        Page {currentPage} of {totalUnitsPages}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.min(totalUnitsPages, p + 1))}
                        disabled={currentPage === totalUnitsPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {activeTab === 'warranties' && (() => {
          const sortedWarranties = getSortedItems(warranties, tabSortConfig);
          const totalWarrantyPages = Math.ceil(sortedWarranties.length / perPage) || 1;
          const paginatedWarranties = sortedWarranties.slice(
            (currentPage - 1) * perPage,
            currentPage * perPage
          );

          return (
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div className="table-controls">
                <div className="table-controls-left">
                  <div className="entries-dropdown">
                    <span>Show</span>
                    <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                      <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>
                </div>
                <div className="table-controls-right">
                  <div className="export-dropdown-container" style={{ position: 'relative' }}>
                    <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Export ▾
                    </button>
                    {showExportDropdown && (
                      <div className="export-dropdown-menu">
                        <button onClick={() => handleExportWarranties('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                        <button onClick={() => handleExportWarranties('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                        <button onClick={() => handleExportWarranties('Print')}><i className="fa fa-print"></i> Print</button>
                        <button onClick={() => handleExportWarranties('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                      </div>
                    )}
                  </div>
                  <button className="control-btn-primary" onClick={() => { setEditingWarranty(null); setShowWarrantyForm(true); setWarrantyForm({ name: '', description: '', duration: '', duration_type: '' }); }}>
                    <span className="plus-icon">+</span> Add Warranty
                  </button>
                </div>
              </div>
              <div className="products-table-container" style={{ overflowY: 'visible' }}>
                {isWarrantiesLoading ? (
                  <div className="loading-sales">
                    <h3>⏳ Loading Warranties...</h3>
                    <p>Fetching warranties data from server...</p>
                  </div>
                ) : paginatedWarranties.length === 0 ? (
                  <div className="no-sales">
                    <h3>📊 No Warranties Found</h3>
                    <p>No warranties have been created yet.</p>
                  </div>
                ) : (
                  <table className="products-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        {renderSortableHeader('name', 'Name')}
                        {renderSortableHeader('description', 'Description')}
                        {renderSortableHeader('duration', 'Duration')}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedWarranties.map((w, idx) => (
                        <tr key={w.id || idx}>
                          <td>{(currentPage - 1) * perPage + idx + 1}</td>
                          <td style={{ fontWeight: '500' }}>{w.name || w}</td>
                          <td>{w.description || '-'}</td>
                          <td>{w.duration ? `${w.duration} ${w.duration_type || 'Months'}` : '-'}</td>
                          <td>
                            <button onClick={() => { setEditingWarranty(w); setShowWarrantyForm(true); setWarrantyForm({ name: w.name || '', description: w.description || '', duration: w.duration || '', duration_type: w.duration_type || 'Months' }); }} style={{ padding: '0.3rem 0.75rem', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.4rem' }}>Edit</button>
                            <button onClick={() => handleDeleteWarranty(w.id)} style={{ padding: '0.3rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Delete</button>
                          </td>
                        </tr>
                      ))
                      }
                    </tbody>
                  </table>
                )}

                {/* Pagination Footer */}
                {!isWarrantiesLoading && sortedWarranties.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                      Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, sortedWarranties.length)} of {sortedWarranties.length} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#64748b' }}>
                        Page {currentPage} of {totalWarrantyPages}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.min(totalWarrantyPages, p + 1))}
                        disabled={currentPage === totalWarrantyPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {activeTab === 'brands' && (() => {
          const sortedBrands = getSortedItems(brands, tabSortConfig);
          const totalBrandsPages = Math.ceil(sortedBrands.length / perPage) || 1;
          const paginatedBrands = sortedBrands.slice(
            (currentPage - 1) * perPage,
            currentPage * perPage
          );

          return (
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div className="table-controls">
                <div className="table-controls-left">
                  <div className="entries-dropdown">
                    <span>Show</span>
                    <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                      <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>
                </div>
                <div className="table-controls-right">
                  <div className="export-dropdown-container" style={{ position: 'relative' }}>
                    <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Export ▾
                    </button>
                    {showExportDropdown && (
                      <div className="export-dropdown-menu">
                        <button onClick={() => handleExportBrands('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                        <button onClick={() => handleExportBrands('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                        <button onClick={() => handleExportBrands('Print')}><i className="fa fa-print"></i> Print</button>
                        <button onClick={() => handleExportBrands('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                      </div>
                    )}
                  </div>
                  <button className="control-btn-primary" onClick={() => { setEditingBrand(null); setShowBrandForm(true); setBrandForm({ name: '', description: '', use_for_repair: false }); }}>
                    <span className="plus-icon">+</span> Add Brand
                  </button>
                </div>
              </div>
              {brandApiError && (
                <div style={{ margin: '0 1rem 0.75rem', padding: '0.65rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{brandApiError}</span>
                  <button onClick={() => { setBrandApiError(null); reloadBrands(); }} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.65rem', cursor: 'pointer', fontSize: '0.8rem' }}>Retry</button>
                </div>
              )}
              <div className="products-table-container" style={{ overflowY: 'visible' }}>
                {isBrandsLoading ? (
                  <div className="loading-sales">
                    <h3>⏳ Loading Brands...</h3>
                    <p>Fetching brands data from server...</p>
                  </div>
                ) : paginatedBrands.length === 0 ? (
                  <div className="no-sales">
                    <h3>📊 No Brands Found</h3>
                    <p>No brands have been created yet.</p>
                  </div>
                ) : (
                  <table className="products-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        {renderSortableHeader('name', 'Name')}
                        {renderSortableHeader('description', 'Description')}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedBrands.map((b, idx) => (
                        <tr key={b.id || idx}>
                          <td>{(currentPage - 1) * perPage + idx + 1}</td>
                          <td style={{ fontWeight: '500' }}>{b.name || b}</td>
                          <td>{b.description || '-'}</td>
                          <td>
                            <button onClick={() => { setEditingBrand(b); setShowBrandForm(true); setBrandForm({ name: b.name || '', description: b.description || '', use_for_repair: !!b.use_for_repair }); }} style={{ padding: '0.3rem 0.75rem', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.4rem' }}>Edit</button>
                            <button onClick={() => handleDeleteBrand(b.id)} style={{ padding: '0.3rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Delete</button>
                          </td>
                        </tr>
                      ))
                      }
                    </tbody>
                  </table>
                )}

                {/* Pagination Footer */}
                {!isBrandsLoading && sortedBrands.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                      Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, sortedBrands.length)} of {sortedBrands.length} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#64748b' }}>
                        Page {currentPage} of {totalBrandsPages}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.min(totalBrandsPages, p + 1))}
                        disabled={currentPage === totalBrandsPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Table Controls Row - only for all_products */}
        {activeTab === 'all_products' && <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => {
                setPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(true)}
                title="Open Filters"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExportProducts('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExportProducts('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExportProducts('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExportProducts('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={handleAddProduct}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>}

        {/* Table Content Card - only for all_products */}
        {activeTab === 'all_products' && (
          <>
            {/* Debug Info / Status Messages */}
            {isLoading && (
              <div className="loading-sales">
                <h3>⏳ Loading Products...</h3>
                <p>Fetching products data from server...</p>
              </div>
            )}

            {error && (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
                <h3>Error loading products</h3>
                <p>{error}</p>
                <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>Retry</button>
              </div>
            )}

            {/* Products Table */}
            {!isLoading && !error && (
              <div className="products-table-container" style={{ overflowY: 'visible' }}>
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      {[
                        { key: 'name', label: 'Product' },
                        { key: 'location', label: 'Business Location' },
                        { key: 'cost', label: 'Unit Purchase Price' },
                        { key: 'price', label: 'Selling Price' },
                        { key: 'currentStock', label: 'Current stock' },
                        { key: 'type', label: 'Product Type' },
                        { key: 'category', label: 'Category' },
                        { key: 'brand', label: 'Brand' },
                        { key: 'tax', label: 'Tax' },
                        { key: 'sku', label: 'SKU' },
                      ].map(({ key, label }) => (
                        <th key={key} className="sortable" onClick={() => handleSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                            {label}
                            {sortConfig.key === key ? (
                              sortConfig.direction === 'asc' ? (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                  <path d="M12 19V5M5 12l7-7 7 7" />
                                </svg>
                              ) : (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                  <path d="M12 5v14M5 12l7 7 7-7" />
                                </svg>
                              )
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M7 15l5 5 5-5" />
                                <path d="M7 9l5-5 5 5" />
                              </svg>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.length === 0 ? (
                      <tr>
                        <td colSpan="11" style={{ textAlign: 'center', padding: '2rem' }}>
                          No products found. (Total fetched: {products.length})
                        </td>
                      </tr>
                    ) : (
                      getSortedProducts(filteredProducts).map((product, idx, arr) => {
                        const stockStatus = getStockStatus(product.currentStock);
                        const isLast = arr.length <= 5 || idx >= arr.length - 4;
                        return (
                          <tr key={product.id} onClick={() => handleViewProduct(product)} style={{ cursor: 'pointer' }}>
                            {/* Action Dropdown */}
                            <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                              <div className={`action-dropdown-container ${isLast ? 'dropup' : ''}`}>
                                <button
                                  className="action-btn-trigger"
                                  onClick={(e) => { e.stopPropagation(); toggleActionMenu(product.id, e); }}
                                >
                                  Actions <span className="arrow-down">▼</span>
                                </button>
                                {openActionId === product.id && ReactDOM.createPortal(
                                  <div
                                    className="action-dropdown-menu"
                                    style={{
                                      position: 'fixed',
                                      top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                      bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                      left: `${dropdownPosition.left}px`,
                                      maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                      zIndex: 9999999
                                    }}
                                  >
                                    <button onClick={() => handleOpenLabelGenerator(product)}>🏷️ Labels</button>
                                    <button onClick={() => handleViewProduct(product)}>👁️ View</button>
                                    <button onClick={() => handleEditProduct(product)}>✏️ Edit</button>
                                    <button onClick={() => handleDeleteProduct(product.id)}>🗑️ Delete</button>
                                    <button onClick={() => handleOpenOpeningStock(product)}>📦 Add or edit opening stock</button>
                                    <button onClick={() => handleOpenGroupPrices(product)}>💰 Add or edit Group Prices</button>
                                    <button onClick={() => handleOpenStockHistory(product)}>📈 Product stock history</button>
                                    <button onClick={() => { /* console.log('Duplicate', product.id) */ }}>📋 Duplicate Product</button>
                                  </div>,
                                  document.body
                                )}
                              </div>
                            </td>

                            {/* Product Name */}
                            <td className="product-name">
                              <strong>{product.name}</strong>
                            </td>

                            {/* Location */}
                            <td>{product.location}</td>

                            {/* Cost */}
                            <td className="cost">${product.cost.toFixed(2)}</td>

                            {/* Price */}
                            <td className="price">${product.price.toFixed(2)}</td>

                            {/* Current Stock */}
                            <td className={`stock-value ${stockStatus.class}`}>
                              {product.currentStock.toFixed(2)} {product.unit}
                            </td>

                            {/* Type */}
                            <td>{product.type}</td>

                            {/* Category */}
                            <td>{product.category}</td>

                            {/* Brand */}
                            <td>{product.brand}</td>

                            {/* Tax */}
                            <td>{product.tax}</td>

                            {/* SKU */}
                            <td className="sku">{product.sku}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>

                {/* Pagination Footer */}
                {!isLoading && !error && products.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                      Showing {filteredProducts.length > 0 ? ((currentPage - 1) * perPage) + 1 : 0} to {Math.min(currentPage * perPage, totalProducts || filteredProducts.length)} of {totalProducts || filteredProducts.length} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                        Page {currentPage} of {lastPage}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                        disabled={currentPage === lastPage}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Selling Price Group Tab */}
        {activeTab === 'selling_price_group' && (() => {
          const sortedSPGs = getSortedItems(sellingPriceGroups, tabSortConfig);
          const totalSPGsPages = Math.ceil(sortedSPGs.length / perPage) || 1;
          const paginatedSPGs = sortedSPGs.slice(
            (currentPage - 1) * perPage,
            currentPage * perPage
          );

          return (
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div className="table-controls">
                <div className="table-controls-left">
                  <div className="entries-dropdown">
                    <span>Show</span>
                    <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                      <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>
                </div>
                <div className="table-controls-right">
                  <div className="export-dropdown-container" style={{ position: 'relative' }}>
                    <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Export ▾
                    </button>
                    {showExportDropdown && (
                      <div className="export-dropdown-menu">
                        <button onClick={() => { alert('Export CSV'); setShowExportDropdown(false); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                        <button onClick={() => { alert('Export Excel'); setShowExportDropdown(false); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                        <button onClick={() => { alert('Print'); setShowExportDropdown(false); }}><i className="fa fa-print"></i> Print</button>
                        <button onClick={() => { alert('Export PDF'); setShowExportDropdown(false); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                      </div>
                    )}
                  </div>
                  <button
                    className="control-btn-primary"
                    onClick={() => { setEditingSPG(null); setSpgForm({ name: '', description: '' }); setShowSPGModal(true); }}
                  >
                    <span className="plus-icon">+</span> Add
                  </button>
                </div>
              </div>

              <div className="products-table-container" style={{ overflowY: 'visible' }}>
                {isSPGLoading ? (
                  <div className="loading-sales">
                    <h3>⏳ Loading Selling Price Groups...</h3>
                    <p>Fetching selling price groups data from server...</p>
                  </div>
                ) : paginatedSPGs.length === 0 ? (
                  <div className="no-sales">
                    <h3>📊 No Selling Price Groups Found</h3>
                    <p>No selling price groups have been created yet.</p>
                  </div>
                ) : (
                  <table className="products-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        {renderSortableHeader('name', 'Name')}
                        {renderSortableHeader('description', 'Description')}
                        <th style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSPGs.map((grp, idx) => (
                        <tr key={grp.id}>
                          <td>{(currentPage - 1) * perPage + idx + 1}</td>
                          <td style={{ color: 'var(--etpos-text, #1e293b)', fontWeight: '500' }}>{grp.name}</td>
                          <td style={{ color: '#6b7280' }}>{grp.description || '—'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                              <button
                                style={{ padding: '0.3rem 0.75rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                                onClick={() => handleEditSPG(grp)}
                              >Edit</button>
                              <button
                                style={{ padding: '0.3rem 0.75rem', background: grp.is_active ? '#eab308' : '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                                onClick={() => handleToggleSPGStatus(grp)}
                              >
                                {grp.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                style={{ padding: '0.3rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                                onClick={() => handleDeleteSPG(grp.id)}
                              >Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))
                      }
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination Controls */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: 'var(--etpos-card, #fff)', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ color: 'var(--etpos-text, #334155)', fontSize: '0.875rem' }}>
                  Showing {paginatedSPGs.length > 0 ? (currentPage - 1) * perPage + 1 : 0} to {Math.min(currentPage * perPage, sortedSPGs.length)} of {sortedSPGs.length} entries
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span style={{ color: 'var(--etpos-text, #334155)', fontSize: '0.875rem' }}>
                    Page {currentPage} of {totalSPGsPages}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalSPGsPages))}
                    disabled={currentPage === totalSPGsPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Add/Edit Product Modal - Removed for Full Page Route */}
      </div>

      {/* Filter Modal Popup */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
            <div className="modal-header">
              <h2>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group">
                  <label>Product Type:</label>
                  <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="all">All</option>
                    {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Category:</label>
                  <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                    <option value="all">All</option>
                    {categories.map(c => <option key={c.id || c.name || c} value={c.name || c}>{c.name || c}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Unit:</label>
                  <select value={filterUnit} onChange={(e) => setFilterUnit(e.target.value)}>
                    <option value="all">All</option>
                    {units.map(u => <option key={u.id || u.actual_name || u} value={u.actual_name || u}>{u.actual_name || u}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Tax:</label>
                  <select value={filterTax} onChange={(e) => setFilterTax(e.target.value)}>
                    <option value="all">All</option>
                    {taxes.map(t => <option key={t.id || t.name || t} value={t.name || t}>{t.name || t}</option>)}
                  </select>
                </div>
              </div>
              <div className="filter-row second-row">
                <div className="filter-group">
                  <label>Brand:</label>
                  <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}>
                    <option value="all">All</option>
                    {brands.map(b => <option key={b.id || b.name || b} value={b.name || b}>{b.name || b}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Business Location:</label>
                  <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)}>
                    <option value="all">All</option>
                    {uniqueLocations.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Status:</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* View Product Modal */}
      {viewModalOpen && selectedProductForView && (
        <div className="modal-overlay printable-modal" onClick={handleCloseViewModal}>
          <div className="modal-content view-product-modal printable-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>#{selectedProductForView.name}</h2>
              <button className="modal-close" onClick={handleCloseViewModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="product-details-grid">
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">SKU:</span>
                    <span className="detail-value">{selectedProductForView.sku || '--'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Category:</span>
                    <span className="detail-value">{selectedProductForView.category || '--'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Brand:</span>
                    <span className="detail-value">{selectedProductForView.brand || '--'}</span>
                  </div>
                </div>

                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Unit:</span>
                    <span className="detail-value">{selectedProductForView.unit || 'Single'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Type:</span>
                    <span className="detail-value">{selectedProductForView.type || 'Single'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Tax:</span>
                    <span className="detail-value">{selectedProductForView.tax || 'None'}</span>
                  </div>
                </div>

                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">{selectedProductForView.location || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Current Stock:</span>
                    <span className="detail-value">{selectedProductForView.currentStock || 0} units</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className="detail-value">{selectedProductForView.status || 'Active'}</span>
                  </div>
                </div>

                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Not for Selling:</span>
                    <span className="detail-value">{selectedProductForView.notForSelling ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Description:</span>
                    <span className="detail-value">{selectedProductForView.description || 'No description'}</span>
                  </div>
                </div>
              </div>

              <div className="product-image-section">
                {selectedProductForView.image ? (
                  <img src={selectedProductForView.image} alt={selectedProductForView.name} className="modal-product-image" />
                ) : (
                  <div className="no-image-placeholder">No Image</div>
                )}
              </div>

              <div className="pricing-table">
                <table>
                  <thead>
                    <tr>
                      <th>Cost Price</th>
                      <th>Selling Price</th>
                      <th>Profit Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>$ {selectedProductForView.cost?.toFixed(2) || '0.00'}</td>
                      <td>$ {selectedProductForView.price?.toFixed(2) || '0.00'}</td>
                      <td>{selectedProductForView.price && selectedProductForView.cost
                        ? ((selectedProductForView.price - selectedProductForView.cost) / selectedProductForView.cost * 100).toFixed(2)
                        : '0.00'}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="stock-details-section">
                <h3 style={{ fontSize: '0.95rem', fontWeight: '700', margin: '1rem 0 0.5rem', color: '#1e293b' }}>Product Stock Details</h3>
                <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
                  <table className="pricing-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: '#10b981', color: 'white' }}>
                        <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left' }}>SKU</th>
                        <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left' }}>Product</th>
                        <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left' }}>Location</th>
                        <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Unit Price</th>
                        <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Current stock</th>
                        <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Current Stock Value</th>
                        <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Total unit sold</th>
                        <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Total Unit Transferred</th>
                        <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Total Unit Adjusted</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ padding: '0.6rem 0.75rem' }}>{selectedProductForView.sku || '--'}</td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>{selectedProductForView.name || '--'}</td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>{selectedProductForView.location || 'N/A'}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>$ {parseFloat(selectedProductForView.price || 0).toFixed(2)}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{parseFloat(selectedProductForView.currentStock || 0).toFixed(2)} {selectedProductForView.unit || 'units'}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>$ {(parseFloat(selectedProductForView.currentStock || 0) * parseFloat(selectedProductForView.cost || 0)).toFixed(2)}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{parseFloat(selectedProductForView.totalUnitSold || selectedProductForView.total_sold || 0).toFixed(2)} {selectedProductForView.unit || 'units'}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{parseFloat(selectedProductForView.totalUnitTransferred || 0).toFixed(2)} {selectedProductForView.unit || 'units'}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{parseFloat(selectedProductForView.totalUnitAdjusted || 0).toFixed(2)} {selectedProductForView.unit || 'units'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-close-modal"
                type="button"
                onClick={() => {
                  handleCloseViewModal();
                  handleEditProduct(selectedProductForView);
                }}
              >
                Edit
              </button>
              <button className="btn-print" onClick={handlePrintProduct}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9"></polyline>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                  <rect x="6" y="14" width="12" height="8"></rect>
                </svg>
                Print
              </button>
              <button className="btn-close-modal" type="button" onClick={handleCloseViewModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Variation Modal */}
      {/* Add/Edit Variation Modal */}
      {showVariationModal && (
        <div className="modal-overlay" onClick={() => { setShowVariationModal(false); setEditingVariation(null); setVariationForm({ name: '', values: [''] }); }}>
          <div className="modal" style={{ maxWidth: '540px', width: '95%', borderRadius: '12px', overflow: 'visible', background: 'var(--etpos-card, #fff)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>
                {editingVariation ? 'Edit Variation' : 'Add Variation'}
              </h3>
              <button
                onClick={() => { setShowVariationModal(false); setEditingVariation(null); setVariationForm({ name: '', values: [''] }); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.25rem', lineHeight: 1, padding: '0.25rem' }}
              >✕</button>
            </div>
            <div style={{ padding: '1.5rem 1.5rem 1rem' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Variation Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Size, Color"
                  value={variationForm.name}
                  onChange={(e) => setVariationForm(f => ({ ...f, name: e.target.value }))}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.6rem' }}>
                  Variation Values <span style={{ color: '#ef4444' }}>*</span>
                </label>
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '0.25rem' }} className="custom-scrollbar">
                {variationForm.values.map((val, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        type="text"
                        placeholder={`Value ${idx + 1} (e.g. Small, Red)`}
                        value={typeof val === 'object' ? val.name : val}
                        onChange={(e) => {
                          const updated = [...variationForm.values];
                          if (typeof val === 'object') {
                            updated[idx] = { ...val, name: e.target.value };
                          } else {
                            updated[idx] = e.target.value;
                          }
                          setVariationForm(f => ({ ...f, values: updated }));
                        }}
                        style={{ width: '100%', padding: '0.6rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                      />
                    </div>
                    {variationForm.values.length > 1 && (
                      <button
                        onClick={() => {
                          if (typeof val === 'object' && val.id) {
                            handleDeleteVariationValue(editingVariation.id, val.id);
                          } else {
                            setVariationForm(f => ({ ...f, values: f.values.filter((_, i) => i !== idx) }));
                          }
                        }}
                        title={typeof val === 'object' && val.id ? "Delete permanently" : "Remove from list"}
                        style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' }}
                      >
                        <i className="fa fa-trash-alt"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => setVariationForm(f => ({ ...f, values: [...f.values, ''] }))}
                style={{ marginTop: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', transition: 'all 0.2s' }}
              >
                <i className="fa fa-plus"></i> Add Another Value
              </button>
            </div>

            {variationApiError && (
              <div style={{ margin: '0 1.5rem 1rem', padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.8rem' }}>
                {variationApiError}
              </div>
            )}

            <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center' }}>
              <button
                onClick={() => { setShowVariationModal(false); setEditingVariation(null); setVariationForm({ name: '', values: [''] }); }}
                style={{ padding: '0.6rem 1.5rem', background: 'var(--etpos-card, #fff)', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
              >Close</button>
              <button
                onClick={handleSaveVariation}
                disabled={isVariationsSaving}
                style={{ padding: '0.6rem 1.75rem', background: isVariationsSaving ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: isVariationsSaving ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', boxShadow: '0 2px 4px rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                {isVariationsSaving ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save"></i> Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Category Modal */}
      {showCategoryForm && (
        <div className="modal-overlay" onClick={() => { setShowCategoryForm(false); setEditingCategory(null); setCategoryForm({ name: '', code: '', description: '', isSubTaxonomy: false, parentCategory: '' }); }}>
          <div className="modal" style={{ maxWidth: '540px', width: '95%', borderRadius: '12px', overflow: 'visible', background: 'var(--etpos-card, #fff)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
              <button onClick={() => { setShowCategoryForm(false); setEditingCategory(null); setCategoryForm({ name: '', code: '', description: '', isSubTaxonomy: false, parentCategory: '' }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.25rem', lineHeight: 1, padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem 1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                    Category Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Category name"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm(f => ({ ...f, name: e.target.value }))}
                    style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                    autoFocus
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>Category Code</label>
                  <input
                    type="text"
                    placeholder="HSN / Short code"
                    value={categoryForm.code}
                    onChange={(e) => setCategoryForm(f => ({ ...f, code: e.target.value }))}
                    style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                  />
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.72rem', color: '#6b7280' }}>Same as HSN code</p>
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>Description</label>
                <textarea
                  placeholder="Description (optional)"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ marginBottom: categoryForm.isSubTaxonomy ? '1rem' : 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', color: 'var(--etpos-text-muted, #374151)' }}>
                  <input
                    type="checkbox"
                    checked={categoryForm.isSubTaxonomy}
                    onChange={(e) => setCategoryForm(f => ({ ...f, isSubTaxonomy: e.target.checked, parentCategory: '' }))}
                    style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#3b82f6' }}
                  />
                  Add as sub taxonomy
                </label>
              </div>
              {categoryForm.isSubTaxonomy && (
                <div style={{ marginTop: '0.75rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>Parent Category</label>
                  <select
                    value={categoryForm.parentCategory}
                    onChange={(e) => setCategoryForm(f => ({ ...f, parentCategory: e.target.value }))}
                    style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none', background: 'var(--etpos-card, #fff)' }}
                  >
                    <option value="">-- Select parent category --</option>
                    {categories.map((cat, idx) => (
                      <option key={idx} value={cat.name || cat}>{cat.name || cat}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {categoryApiError && (
              <div style={{ margin: '0 1.5rem 1rem', padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.8rem' }}>
                {categoryApiError}
              </div>
            )}

            <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center' }}>
              <button
                onClick={() => { setShowCategoryForm(false); setEditingCategory(null); setCategoryForm({ name: '', code: '', description: '', isSubTaxonomy: false, parentCategory: '' }); }}
                style={{ padding: '0.6rem 1.5rem', background: 'var(--etpos-card, #fff)', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
              >Close</button>
              <button
                onClick={handleSaveCategory}
                disabled={isCategoriesSaving}
                style={{ padding: '0.6rem 1.75rem', background: isCategoriesSaving ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: isCategoriesSaving ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', boxShadow: '0 2px 4px rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                {isCategoriesSaving ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save"></i> Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Unit Modal */}
      {showUnitForm && (
        <div className="modal-overlay" onClick={() => { setShowUnitForm(false); setEditingUnit(null); setUnitForm({ actual_name: '', short_name: '', allow_decimal: '' }); }}>
          <div className="modal" style={{ maxWidth: '540px', width: '95%', borderRadius: '12px', overflow: 'visible', background: 'var(--etpos-card, #fff)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>{editingUnit ? 'Edit Unit' : 'Add Unit'}</h3>
              <button onClick={() => { setShowUnitForm(false); setEditingUnit(null); setUnitForm({ actual_name: '', short_name: '', allow_decimal: '' }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.25rem', lineHeight: 1, padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem 1.5rem' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Unit name (e.g. Pieces)"
                  value={unitForm.actual_name}
                  onChange={(e) => setUnitForm(f => ({ ...f, actual_name: e.target.value }))}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Short Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Short name (e.g. Pc(s))"
                  value={unitForm.short_name}
                  onChange={(e) => setUnitForm(f => ({ ...f, short_name: e.target.value }))}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Allow Decimal <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={unitForm.allow_decimal}
                  onChange={(e) => setUnitForm(f => ({ ...f, allow_decimal: e.target.value }))}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text-muted, #475569)' }}
                >
                  <option value="">Please Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>

            {unitApiError && (
              <div style={{ margin: '0 1.5rem 1rem', padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.8rem' }}>
                {unitApiError}
              </div>
            )}

            <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center' }}>
              <button
                onClick={() => { setShowUnitForm(false); setEditingUnit(null); setUnitForm({ actual_name: '', short_name: '', allow_decimal: '' }); }}
                style={{ padding: '0.6rem 1.5rem', background: 'var(--etpos-card, #fff)', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
              >Close</button>
              <button
                onClick={handleSaveUnit}
                disabled={isUnitsSaving}
                style={{ padding: '0.6rem 1.75rem', background: isUnitsSaving ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: isUnitsSaving ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', boxShadow: '0 2px 4px rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                {isUnitsSaving ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save"></i> Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Warranty Modal */}
      {showWarrantyForm && (
        <div className="modal-overlay" onClick={() => { setShowWarrantyForm(false); setEditingWarranty(null); setWarrantyForm({ name: '', description: '', duration: '', duration_type: '' }); }}>
          <div className="modal" style={{ maxWidth: '540px', width: '95%', borderRadius: '12px', overflow: 'visible', background: 'var(--etpos-card, #fff)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>{editingWarranty ? 'Edit Warranty' : 'Add Warranty'}</h3>
              <button onClick={() => { setShowWarrantyForm(false); setEditingWarranty(null); setWarrantyForm({ name: '', description: '', duration: '', duration_type: '' }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.25rem', lineHeight: 1, padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem 1.5rem' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Warranty name"
                  value={warrantyForm.name}
                  onChange={(e) => setWarrantyForm(f => ({ ...f, name: e.target.value }))}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Description
                </label>
                <textarea
                  placeholder="Description (optional)"
                  value={warrantyForm.description}
                  onChange={(e) => setWarrantyForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none', resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Duration <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden', background: 'var(--etpos-card, #fff)' }}>
                  <input
                    type="number"
                    placeholder="Duration"
                    value={warrantyForm.duration}
                    onChange={(e) => setWarrantyForm(f => ({ ...f, duration: e.target.value }))}
                    style={{ flex: 1, padding: '0.65rem 0.85rem', border: 'none', borderRight: '1px solid #d1d5db', fontSize: '0.875rem', outline: 'none', width: '50%' }}
                  />
                  <select
                    value={warrantyForm.duration_type}
                    onChange={(e) => setWarrantyForm(f => ({ ...f, duration_type: e.target.value }))}
                    style={{ flex: 1, padding: '0.65rem 0.85rem', border: 'none', fontSize: '0.875rem', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text-muted, #475569)', width: '50%' }}
                  >
                    <option value="">Please Select</option>
                    <option value="Days">Days</option>
                    <option value="Months">Months</option>
                    <option value="Years">Years</option>
                  </select>
                </div>
              </div>
            </div>

            {warrantyApiError && (
              <div style={{ margin: '0 1.5rem 1rem', padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.8rem' }}>
                {warrantyApiError}
              </div>
            )}

            <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center' }}>
              <button
                onClick={() => { setShowWarrantyForm(false); setEditingWarranty(null); setWarrantyForm({ name: '', description: '', duration: '', duration_type: '' }); }}
                style={{ padding: '0.6rem 1.5rem', background: 'var(--etpos-card, #fff)', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
              >Close</button>
              <button
                onClick={handleSaveWarranty}
                disabled={isWarrantiesSaving}
                style={{ padding: '0.6rem 1.75rem', background: isWarrantiesSaving ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: isWarrantiesSaving ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', boxShadow: '0 2px 4px rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                {isWarrantiesSaving ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save"></i> Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Brand Modal */}
      {showBrandForm && (
        <div className="modal-overlay" onClick={() => { setShowBrandForm(false); setEditingBrand(null); setBrandForm({ name: '', description: '', use_for_repair: false }); }}>
          <div className="modal" style={{ maxWidth: '540px', width: '95%', borderRadius: '12px', overflow: 'visible', background: 'var(--etpos-card, #fff)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>{editingBrand ? 'Edit Brand' : 'Add Brand'}</h3>
              <button onClick={() => { setShowBrandForm(false); setEditingBrand(null); setBrandForm({ name: '', description: '', use_for_repair: false }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.25rem', lineHeight: 1, padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem 1.5rem' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Brand Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Brand name"
                  value={brandForm.name}
                  onChange={(e) => setBrandForm(f => ({ ...f, name: e.target.value }))}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Short Description
                </label>
                <input
                  type="text"
                  placeholder="Short description (optional)"
                  value={brandForm.description}
                  onChange={(e) => setBrandForm(f => ({ ...f, description: e.target.value }))}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
              <div style={{ marginBottom: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--etpos-text, #374151)', fontWeight: '500' }}>
                  <input
                    type="checkbox"
                    checked={brandForm.use_for_repair}
                    onChange={(e) => setBrandForm(f => ({ ...f, use_for_repair: e.target.checked }))}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6' }}
                  />
                  Use for repair? <i className="fa fa-info-circle" style={{ color: '#0ea5e9' }}></i>
                </label>
              </div>
            </div>

            {brandApiError && (
              <div style={{ margin: '0 1.5rem 1rem', padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.8rem' }}>
                {brandApiError}
              </div>
            )}

            <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center' }}>
              <button
                onClick={() => { setShowBrandForm(false); setEditingBrand(null); setBrandForm({ name: '', description: '', use_for_repair: false }); }}
                style={{ padding: '0.6rem 1.5rem', background: 'var(--etpos-card, #fff)', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
              >Close</button>
              <button
                onClick={handleSaveBrand}
                disabled={isBrandsSaving}
                style={{ padding: '0.6rem 1.75rem', background: isBrandsSaving ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: isBrandsSaving ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', boxShadow: '0 2px 4px rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                {isBrandsSaving ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save"></i> Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Add Selling Price Group Modal */}
      {showSPGModal && (
        <div className="modal-overlay" onClick={() => setShowSPGModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px', width: '95%' }}>
            <div className="modal-header">
              <h3>{editingSPG ? 'Edit Selling Price Group' : 'Add Selling Price Group'}</h3>
              <button className="modal-close-btn" onClick={() => setShowSPGModal(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Name: <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Name"
                  value={spgForm.name}
                  onChange={e => setSpgForm(f => ({ ...f, name: e.target.value }))}
                  style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text-muted, #374151)', marginBottom: '0.4rem' }}>
                  Description:
                </label>
                <textarea
                  className="form-textarea"
                  placeholder="Description"
                  value={spgForm.description}
                  onChange={e => setSpgForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap' }}>
              {spgApiError && (
                <div style={{ width: '100%', marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#991b1b', fontSize: '0.8rem' }}>
                  {spgApiError}
                </div>
              )}
              <button
                onClick={() => { setShowSPGModal(false); setEditingSPG(null); setSpgForm({ name: '', description: '' }); }}
                style={{ padding: '0.6rem 1.75rem', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text-muted, #374151)', border: '1px solid #d1d5db', borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={handleSaveSPG}
                disabled={isSPGSaving}
                style={{ padding: '0.6rem 1.75rem', background: isSPGSaving ? '#93c5fd' : '#3b82f6', color: 'var(--etpos-card, #fff)', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem', cursor: isSPGSaving ? 'not-allowed' : 'pointer' }}
              >
                {isSPGSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Label Generator Modal */}
      <LabelGenerator
        isOpen={showLabelModal}
        onClose={() => {
          setShowLabelModal(false);
          setSelectedProductForLabel(null);
        }}
        initialProducts={selectedProductForLabel}
      />

      {/* Product Stock History Modal */}
      {showStockHistoryModal && (
        <StockHistoryModal
          product={selectedProductForHistory}
          onClose={() => {
            setShowStockHistoryModal(false);
            setSelectedProductForHistory(null);
          }}
        />
      )}

      {/* Group Prices Modal */}
      {showGroupPricesModal && (
        <GroupPricesModal
          product={selectedProductForGroupPrices}
          sellingPriceGroups={sellingPriceGroups}
          onClose={() => {
            setShowGroupPricesModal(false);
            setSelectedProductForGroupPrices(null);
          }}
          onSave={handleSaveGroupPrices}
          onSaveAndAddOpeningStock={handleSaveAndAddOpeningStock}
        />
      )}

      {/* Opening Stock Modal */}
      {showOpeningStockModal && (
        <OpeningStockModal
          product={selectedProductForOpeningStock}
          locations={businessLocations}
          onClose={() => {
            setShowOpeningStockModal(false);
            setSelectedProductForOpeningStock(null);
          }}
          onSave={handleSaveOpeningStock}
        />
      )}
    </div>
  );
};

export default Products;
